import type { CSSProperties, MutableRefObject, ReactNode } from "react";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { IconArrowUpLeft } from "@tabler/icons-react";
import {
  AlertCircleIcon,
  ArchiveIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  BookmarkIcon,
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CommandIcon,
  DollarSignIcon,
  DownloadIcon,
  FileTextIcon,
  FilterIcon,
  MoreVerticalIcon,
  PencilIcon,
  SearchIcon,
  SendIcon,
  UserIcon,
  UsersRoundIcon,
  UsersIcon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { domainRouterClient } from "../../../api/domainRouterClient";
import {
  openTextPdfInNewTab,
  openTuitionFamiliesReportPdfInNewTab,
} from "../../../utils/pdf";
import { TailwindDateField } from "../../../components/forms/TailwindDateField";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import {
  formatCurrencyThousands,
  sourceFaithfulChartAxisTick,
  sourceFaithfulChartLegendStyle,
  sourceFaithfulChartTooltipStyle,
  sourceFaithfulPalette,
} from "../../source-faithful/shared/SourceFaithfulRecharts";
import {
  type TuitionDashboardSnapshot,
  type TuitionDocumentSnapshot,
  type TuitionFamilySnapshot,
  type TuitionFamilyLedgerSnapshot,
  type TuitionPayerSnapshot,
  type TuitionStudentSummary,
} from "../shared/tuitionData";
import { ACTIVE_TUITION_ACADEMIC_YEAR } from "../shared/academicYear";
import {
  useSchoolTuitionDashboardObjectTree,
  useSchoolTuitionFamilyLedgerObjectTree,
} from "../shared/useTuitionObjectTrees";
import {
  useCreateAdHocChargeMutation,
  useCreateBulkChargeMutation,
  useLazyGetBulkChargeJobQuery,
  useLazyPreviewBulkChargeSegmentQuery,
  useIssueTuitionRefundMutation,
  useReverseTuitionPaymentMutation,
} from "../../../state/domainRouterApi";
import {
  filterCollectionTrendProjection,
  resolveCollectionTrendAxisBounds,
  resolveCollectionTrendProjection,
  type CollectionTrendPreset,
  type SchoolCollectionTrendDatum,
} from "./collectionTrendProjection";
import { RecurringFeeAssignment } from "./RecurringFeeAssignment";
import { extractTuitionActionErrorMessage } from "./tuitionActionErrorMessage";

const ACADEMIC_YEAR = ACTIVE_TUITION_ACADEMIC_YEAR;
const TOAST_VISIBLE_MS = 5000;
const TOAST_FADE_MS = 1000;

type FixedLayerPosition = {
  top: number;
  left?: number;
  right?: number;
  minWidth?: number;
};

const buildFixedLayerPosition = (
  element: HTMLElement,
  options: {
    align?: "left" | "right";
    offsetY?: number;
    minWidth?: number;
    matchTriggerWidth?: boolean;
  } = {},
): FixedLayerPosition => {
  const {
    align = "left",
    offsetY = 4,
    minWidth,
    matchTriggerWidth = false,
  } = options;
  const rect = element.getBoundingClientRect();
  return {
    top: rect.bottom + offsetY,
    left: align === "left" ? rect.left : undefined,
    right: align === "right" ? window.innerWidth - rect.right : undefined,
    minWidth: matchTriggerWidth ? rect.width : minWidth,
  };
};

function FixedLayerPortalMenu({
  position,
  portalRef,
  children,
  className,
}: {
  position: FixedLayerPosition | null;
  portalRef?: MutableRefObject<HTMLDivElement | null>;
  children: ReactNode;
  className: string;
}) {
  if (typeof document === "undefined" || position == null) {
    return null;
  }

  return createPortal(
    <div
      ref={portalRef}
      className={className}
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        right: position.right,
        minWidth: position.minWidth,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

function BackToDashboardLink() {
  return (
    <Link
      id="back-to-dashboard"
      to="/school/tuition"
      className="items-center justify-center gap-2 text-sm font-medium text-text-secondary transition-colors hover:text-primary"
    >
      <IconArrowUpLeft className="h-4 w-4" stroke={2} />
      Back to dashboard
    </Link>
  );
}

type ParsedDashboardSearch = {
  familyTerms: string[];
  studentTerms: string[];
  statusTerms: string[];
  dueDateTerms: string[];
  generalTerms: string[];
};
type ReportingWindowPreset = "day" | "week" | "month" | "custom";

type ReportingWindow = {
  startIsoDate: string;
  endIsoDate: string;
  label: string;
};

type ManualSettlementDraft = {
  amount: string;
  instrument: "check" | "ach" | "wire" | "manual_card";
  referenceCode: string;
  notes: string;
  effectiveDate: string;
};

type BillingCategoryValue =
  | "tuition"
  | "fees"
  | "activities"
  | "transportation"
  | "food_service"
  | "other";

type SchoolLedgerType =
  | "Charge"
  | "Payment"
  | "Refund"
  | "Reversal"
  | "Chargeback"
  | "Credit"
  | "Late fee"
  | "Adjustment";

type SchoolLedgerDateRange =
  | "all"
  | "this-month"
  | "last-month"
  | "last-3-months";

type SchoolLedgerEntry = {
  id: string;
  date: string;
  description: string;
  type: SchoolLedgerType;
  entryType: string;
  amountCents: number;
  runningBalanceCents: number;
  source: "intellio" | "external";
  sourceLabel: string;
  studentName: string | null;
  relatedEntryId: string | null;
  disputeId: string | null;
  disputeDeadline: string | null;
  isReversed: boolean;
  billingCategory: BillingCategoryValue | null;
  adhocInvoiceId: string | null;
  archived?: boolean;
};

type SchoolLedgerEditDraft = {
  date?: string;
  description?: string;
  type?: SchoolLedgerType;
  amount?: number;
  source?: SchoolLedgerEntry["source"];
  studentName?: string;
};

type RecordPaymentFormState = {
  method: "Check" | "Cash" | "ACH" | "Wire" | "Manual card capture" | "Other";
  amount: string;
  studentId: string;
  period: string;
  receivedDate: string;
  referenceCode: string;
  notes: string;
};

type RecordPaymentModalView = "entry" | "process";

type PayorFormState = {
  relationLabel: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  contributionType: "$" | "%";
  contributionValue: string;
};

type FamilyLedgerInitialOverlay =
  | "payors"
  | "record-payment"
  | "recurring-fees"
  | null;

type ReversePaymentFormState = {
  reason: string;
  error: string | null;
};

type IssueRefundFormState = {
  amount: string;
  reason: "WITHDRAWAL" | "OVERPAYMENT" | "BILLING_ERROR" | "OTHER";
  reasonNotes: string;
  method: "STRIPE_ORIGINAL" | "CHECK" | "OTHER";
  originalPaymentEntryId: string;
  referenceNumber: string;
  error: string | null;
};

type AddChargeFormState = {
  chargeName: string;
  amount: string;
  perStudent: boolean;
  studentIds: string[];
  dueDate: string;
  billingCategory: BillingCategoryValue;
  notes: string;
  error: string | null;
};

type BulkChargeFormState = {
  chargeName: string;
  amountPerStudent: string;
  dueDate: string;
  billingCategory: BillingCategoryValue;
  notes: string;
  grades: string[];
  divisions: string[];
  gender: "ALL" | "BOYS" | "GIRLS";
  rateCategories: string[];
  showStudents: boolean;
  confirm: boolean;
  error: string | null;
};

type BulkChargeSegmentFilters = Pick<
  BulkChargeFormState,
  "grades" | "divisions" | "gender" | "rateCategories"
>;

type SchoolTuitionFamilyNavigationState = {
  familySnapshot?: TuitionFamilySnapshot | null;
  initialOverlay?: FamilyLedgerInitialOverlay;
};

type TuitionToastVariant = "error" | "success";

type TuitionToast = {
  id: string;
  message: string;
  variant: TuitionToastVariant;
  testId?: string;
  exiting: boolean;
};

const formatCurrency = (cents: number): string =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const formatCurrencyWithSign = (cents: number): string =>
  cents < 0 ? `-${formatCurrency(Math.abs(cents))}` : formatCurrency(cents);

const formatPercent = (value: number): string => `${Math.round(value * 100)}%`;

const formatDate = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "Date unavailable";
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCurrencyDollars = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);

const toSentenceCase = (value: string): string =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ") || "Ledger entry";

const normalizeFileSegment = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "file";

const toIsoDate = (value: unknown): string | null => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString().slice(0, 10);
};

const resolveLedgerType = (
  transaction: TuitionFamilySnapshot["transactions"][number],
): SchoolLedgerType => {
  switch (transaction.entryType) {
    case "payment":
      return "Payment";
    case "refund":
      return "Refund";
    case "reversal":
      return "Reversal";
    case "chargeback":
      return "Chargeback";
    case "credit":
    case "scholarship":
      return "Credit";
    case "late_fee":
      return "Late fee";
    case "adjustment":
      return "Adjustment";
    default:
      return transaction.amountCents < 0 ? "Payment" : "Charge";
  }
};

const resolveLedgerSource = (
  transaction: TuitionFamilySnapshot["transactions"][number],
): SchoolLedgerEntry["source"] => {
  if (transaction.source === "system") {
    return "intellio";
  }
  if ((transaction.source ?? "").startsWith("stripe_")) {
    return "intellio";
  }
  if ((transaction.source ?? "").startsWith("manual_")) {
    return "external";
  }
  if (transaction.sourceKind === "stripe-correlated") {
    return "intellio";
  }
  const normalizedDescription = String(
    transaction.description ?? "",
  ).toLowerCase();
  return normalizedDescription.includes("manual") ||
    normalizedDescription.includes("check")
    ? "external"
    : "intellio";
};

const resolveLedgerSourceLabel = (
  transaction: TuitionFamilySnapshot["transactions"][number],
): string => {
  switch (transaction.source) {
    case "manual_check":
      return transaction.referenceCode
        ? `Check #${transaction.referenceCode}`
        : "Manual check";
    case "manual_cash":
      return "Cash";
    case "manual_ach":
      return transaction.referenceCode
        ? `ACH ${transaction.referenceCode}`
        : "ACH";
    case "manual_wire":
      return transaction.referenceCode
        ? `Wire ${transaction.referenceCode}`
        : "Wire";
    case "manual_bank_transfer":
      return transaction.referenceCode
        ? `Bank transfer ${transaction.referenceCode}`
        : "Bank transfer";
    case "manual_other":
      return transaction.referenceCode
        ? `Manual ref ${transaction.referenceCode}`
        : "Manual other";
    case "system":
      return "System generated charge";
    case "stripe_refund":
      return "Stripe refund";
    case "stripe_chargeback":
      return "Stripe chargeback";
    case "stripe_bank_account":
      return "Stripe bank account";
    case "stripe_card":
      return "Stripe card";
    default:
      return transaction.sourceKind === "stripe-correlated"
        ? "Stripe payment method"
        : transaction.referenceCode
          ? `Reference ${transaction.referenceCode}`
          : "Internal ledger";
  }
};

const buildFamilyLedgerEntries = (
  family: TuitionFamilySnapshot,
): SchoolLedgerEntry[] => {
  const sortedTransactions = [...family.transactions].sort((left, right) => {
    const leftMoment =
      Date.parse(
        left.observedAt ?? left.internalEffectiveDate ?? left.createdAt ?? "",
      ) || 0;
    const rightMoment =
      Date.parse(
        right.observedAt ??
          right.internalEffectiveDate ??
          right.createdAt ??
          "",
      ) || 0;
    return leftMoment - rightMoment;
  });

  let runningBalanceCents = 0;
  return sortedTransactions.map((transaction, index) => {
    runningBalanceCents += transaction.amountCents;
    const matchingStudent =
      family.students.find((student) => student.id === transaction.studentId) ??
      null;
    return {
      id: transaction.id || `${family.familyId}-ledger-${index + 1}`,
      date:
        toIsoDate(
          transaction.observedAt ??
            transaction.internalEffectiveDate ??
            transaction.createdAt,
        ) ?? new Date().toISOString().slice(0, 10),
      description:
        String(transaction.description ?? "").trim() ||
        toSentenceCase(transaction.entryType),
      type: resolveLedgerType(transaction),
      entryType: transaction.entryType,
      amountCents: transaction.amountCents,
      runningBalanceCents,
      source: resolveLedgerSource(transaction),
      sourceLabel: resolveLedgerSourceLabel(transaction),
      studentName: matchingStudent?.name ?? null,
      relatedEntryId: transaction.relatedEntryId,
      disputeId:
        transaction.disputeId ??
        transaction.stripeCorrelation?.disputeId ??
        null,
      disputeDeadline: transaction.disputeDeadline,
      isReversed: transaction.isReversed,
      billingCategory:
        (transaction.billingCategory as BillingCategoryValue | null) ?? null,
      adhocInvoiceId: transaction.adhocInvoiceId ?? null,
    };
  });
};

const buildSeedFamilyLedgerSnapshot = (
  academicYear: string,
  family: TuitionFamilySnapshot,
): TuitionFamilyLedgerSnapshot => ({
  academicYear,
  family,
  ledgerEntries: family.transactions,
  payers: family.payers,
  documents: family.documents,
  summary: {
    totalChargesCents: family.totalChargesCents,
    totalCreditsCents: family.totalCreditsCents,
    netBalanceCents: family.netBalanceCents,
    currentDueCents: family.currentDueCents,
    overdueCents: family.overdueCents,
    studentCount: family.studentCount,
    autoPayEnrolled: family.autoPayEnrolled,
    paymentMethodSummary: family.paymentMethodSummary,
    statusLabel: family.statusLabel,
    nextDueDate: family.nextDueDate,
    nextDueDateLabel: family.nextDueDateLabel,
    nextDueDateState: family.nextDueDateState,
    atRisk: family.atRisk,
  },
  latestObservedAt: family.latestObservedAt,
  hasCorrelatedStripeData: family.hasCorrelatedStripeData,
});

const getLedgerTypeBadgeColor = (type: SchoolLedgerType): string => {
  switch (type) {
    case "Charge":
      return "bg-muted text-iron-grey border-border";
    case "Payment":
      return "bg-success-background text-jungle border-aquamarine/30";
    case "Refund":
      return "bg-[rgba(38,151,162,0.08)] text-primary border-primary/20";
    case "Reversal":
      return "bg-[rgba(254,44,59,0.08)] text-strawberry border-[rgba(254,44,59,0.2)]";
    case "Chargeback":
      return "bg-[rgba(254,44,59,0.08)] text-strawberry border-[rgba(254,44,59,0.2)]";
    case "Credit":
      return "bg-info-background text-primary border-primary/30";
    case "Late fee":
      return "bg-warning-background text-coral border-coral/30";
    default:
      return "bg-muted text-text-secondary border-border";
  }
};

const getLedgerDateRangeLabel = (range: SchoolLedgerDateRange): string => {
  switch (range) {
    case "this-month":
      return "This month";
    case "last-month":
      return "Last month";
    case "last-3-months":
      return "Last 3 months";
    default:
      return "All dates";
  }
};

const ledgerEntryMatchesDateRange = (
  entry: SchoolLedgerEntry,
  range: SchoolLedgerDateRange,
): boolean => {
  if (range === "all") {
    return true;
  }

  const entryDate = new Date(`${entry.date}T00:00:00Z`);
  const now = new Date();
  if (range === "this-month") {
    return (
      entryDate.getUTCMonth() === now.getUTCMonth() &&
      entryDate.getUTCFullYear() === now.getUTCFullYear()
    );
  }
  if (range === "last-month") {
    const lastMonth = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1),
    );
    return (
      entryDate.getUTCMonth() === lastMonth.getUTCMonth() &&
      entryDate.getUTCFullYear() === lastMonth.getUTCFullYear()
    );
  }
  const threeMonthsAgo = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1),
  );
  return entryDate >= threeMonthsAgo;
};

const buildRecordPaymentDefaults = (
  family: TuitionFamilySnapshot,
): RecordPaymentFormState => ({
  method: "Check",
  amount: "",
  studentId: "",
  period: "",
  receivedDate: new Date().toISOString().slice(0, 10),
  referenceCode: "",
  notes: "",
});

const buildReversePaymentDefaults = (): ReversePaymentFormState => ({
  reason: "",
  error: null,
});

const buildIssueRefundDefaults = (): IssueRefundFormState => ({
  amount: "",
  reason: "OVERPAYMENT",
  reasonNotes: "",
  method: "STRIPE_ORIGINAL",
  originalPaymentEntryId: "",
  referenceNumber: "",
  error: null,
});

const buildAddChargeDefaults = (): AddChargeFormState => ({
  chargeName: "",
  amount: "",
  perStudent: false,
  studentIds: [],
  dueDate: new Date().toISOString().slice(0, 10),
  billingCategory: "fees",
  notes: "",
  error: null,
});

const buildBulkChargeDefaults = (): BulkChargeFormState => ({
  chargeName: "",
  amountPerStudent: "",
  dueDate: new Date().toISOString().slice(0, 10),
  billingCategory: "fees",
  notes: "",
  grades: [],
  divisions: [],
  gender: "ALL",
  rateCategories: [],
  showStudents: false,
  confirm: false,
  error: null,
});

const formatBillingCategoryLabel = (value: BillingCategoryValue): string =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const toggleSelection = (values: string[], candidate: string): string[] =>
  values.includes(candidate)
    ? values.filter((value) => value !== candidate)
    : [...values, candidate];

const resolveBulkChargePreviewErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object") {
    if (
      "message" in error &&
      typeof error.message === "string" &&
      error.message.trim()
    ) {
      return error.message;
    }
    if (
      "error" in error &&
      typeof error.error === "string" &&
      error.error.trim()
    ) {
      return error.error;
    }
    if (
      "data" in error &&
      error.data &&
      typeof error.data === "object" &&
      "message" in error.data &&
      typeof error.data.message === "string" &&
      error.data.message.trim()
    ) {
      return error.data.message;
    }
  }
  return "Unable to load live preview.";
};

const normalizeBulkChargeStudentGender = (
  value: string | null | undefined,
): "male" | "female" | null => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "male" || normalized === "boy" || normalized === "m") {
    return "male";
  }
  if (normalized === "female" || normalized === "girl" || normalized === "f") {
    return "female";
  }
  return null;
};

const isActiveBulkChargeStudent = (
  status: string | null | undefined,
): boolean => {
  const normalized = String(status ?? "active")
    .trim()
    .toLowerCase();
  return !normalized || normalized === "active";
};

const matchesBulkChargeStudentFilters = (
  student: TuitionStudentSummary,
  filters: BulkChargeSegmentFilters,
): boolean => {
  if (
    filters.grades.length > 0 &&
    !filters.grades.includes(student.gradeLabel)
  ) {
    return false;
  }
  if (
    filters.divisions.length > 0 &&
    !filters.divisions.includes(student.divisionLabel)
  ) {
    return false;
  }
  if (
    filters.rateCategories.length > 0 &&
    !filters.rateCategories.includes(student.rateCategory ?? "")
  ) {
    return false;
  }
  if (filters.gender === "BOYS") {
    return normalizeBulkChargeStudentGender(student.gender) === "male";
  }
  if (filters.gender === "GIRLS") {
    return normalizeBulkChargeStudentGender(student.gender) === "female";
  }
  return true;
};

const buildFallbackBulkChargePreview = (
  snapshot: TuitionDashboardSnapshot | null,
  filters: BulkChargeSegmentFilters,
) => {
  const students = (snapshot?.families ?? [])
    .flatMap((family) =>
      family.students.map((student) => ({
        id: student.id,
        name: student.name,
        grade: student.gradeLabel || null,
        familyName: family.familyName,
        tuitionAccountId: family.familyId,
        status: student.status ?? null,
        gender: student.gender ?? null,
        divisionLabel: student.divisionLabel,
        rateCategory: student.rateCategory ?? null,
        gradeLabel: student.gradeLabel,
      })),
    )
    .filter(
      (student) =>
        isActiveBulkChargeStudent(student.status) &&
        matchesBulkChargeStudentFilters(
          {
            id: student.id,
            name: student.name,
            gradeLabel: student.gradeLabel,
            divisionLabel: student.divisionLabel,
            rateCategory: student.rateCategory,
            gender: student.gender,
            status: student.status,
          },
          filters,
        ),
    )
    .sort(
      (left, right) =>
        left.familyName.localeCompare(right.familyName) ||
        left.name.localeCompare(right.name),
    );

  return {
    studentCount: students.length,
    familyCount: new Set(students.map((student) => student.tuitionAccountId))
      .size,
    students: students.map(({ id, name, grade, familyName }) => ({
      id,
      name,
      grade,
      familyName,
    })),
  };
};

const isStripePaymentEligibleForRefund = (
  transaction: TuitionFamilySnapshot["transactions"][number],
  now = Date.now(),
): boolean => {
  if (transaction.entryType !== "payment") {
    return false;
  }
  const paymentIntentId =
    transaction.stripeCorrelation?.paymentIntentId ??
    (transaction.referenceCode?.startsWith("stripe:tuition:payment:")
      ? transaction.referenceCode.slice("stripe:tuition:payment:".length)
      : null);
  if (!paymentIntentId) {
    return false;
  }
  const observedAt = Date.parse(
    transaction.observedAt ??
      transaction.internalEffectiveDate ??
      transaction.createdAt ??
      "",
  );
  if (!Number.isFinite(observedAt)) {
    return false;
  }
  const ageMs = now - observedAt;
  return ageMs >= 0 && ageMs <= 180 * 24 * 60 * 60 * 1000;
};

const resolvePrimaryPayerName = (family: TuitionFamilySnapshot): string =>
  family.payers.find((payer) => payer.isPrimary)?.displayName ??
  family.payers[0]?.displayName ??
  family.familyName;

const extractChargebackReason = (description: string | null): string => {
  const match = String(description ?? "").match(/reason:\s*(.+)$/i);
  return match?.[1]?.trim() || "Unknown";
};

const buildPayorFormDefaults = (): PayorFormState => ({
  relationLabel: "Sponsor",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  contributionType: "$",
  contributionValue: "",
});

const shiftIsoDate = (isoDate: string, deltaDays: number): string => {
  const cursor = new Date(`${isoDate}T00:00:00Z`);
  cursor.setUTCDate(cursor.getUTCDate() + deltaDays);
  return cursor.toISOString().slice(0, 10);
};

const resolveAnchorIsoDate = (
  snapshot: TuitionDashboardSnapshot | null,
): string => {
  const latestObservedAt = String(snapshot?.latestObservedAt ?? "").trim();
  return toIsoDate(latestObservedAt) ?? new Date().toISOString().slice(0, 10);
};

const resolveReportingWindow = (
  preset: ReportingWindowPreset,
  anchorIsoDate: string,
  customStart: string,
  customEnd: string,
): ReportingWindow => {
  if (preset === "custom") {
    const startIsoDate = customStart || anchorIsoDate;
    const endIsoDate = customEnd || anchorIsoDate;
    return {
      startIsoDate: startIsoDate <= endIsoDate ? startIsoDate : endIsoDate,
      endIsoDate: endIsoDate >= startIsoDate ? endIsoDate : startIsoDate,
      label: `${formatDate(startIsoDate)} to ${formatDate(endIsoDate)}`,
    };
  }

  const dayCount = preset === "day" ? 1 : preset === "week" ? 7 : 30;
  const startIsoDate = shiftIsoDate(anchorIsoDate, -(dayCount - 1));
  return {
    startIsoDate,
    endIsoDate: anchorIsoDate,
    label: `${
      preset === "day" ? "1 day" : preset === "week" ? "1 week" : "1 month"
    } ending ${formatDate(anchorIsoDate)}`,
  };
};

const resolveTransactionMoment = (
  transaction: TuitionFamilySnapshot["transactions"][number],
): string | null =>
  toIsoDate(
    transaction.observedAt ??
      transaction.internalEffectiveDate ??
      transaction.createdAt,
  );

const transactionFallsWithinWindow = (
  transaction: TuitionFamilySnapshot["transactions"][number],
  window: ReportingWindow,
): boolean => {
  const moment = resolveTransactionMoment(transaction);
  return Boolean(
    moment && moment >= window.startIsoDate && moment <= window.endIsoDate,
  );
};

const csvEscape = (value: string | number | null): string => {
  const normalized = String(value ?? "");
  return /[",\n]/u.test(normalized)
    ? `"${normalized.replace(/"/g, '""')}"`
    : normalized;
};

const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const uploadFileToSignedUrl = async (
  uploadUrl: string,
  file: File,
): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}.`);
  }
};

const buildAccountingExportCsv = (family: TuitionFamilySnapshot): string => {
  const rows = [
    [
      "Family",
      "Student",
      "Date",
      "Entry Type",
      "Description",
      "Reference",
      "Amount",
    ],
    ...family.transactions.map((transaction) => [
      family.familyName,
      transaction.studentId ?? "Family-level",
      transaction.observedDateLabel,
      transaction.entryType,
      transaction.description ?? "",
      transaction.referenceCode ?? "",
      String(transaction.amountCents / 100),
    ]),
  ];
  return rows
    .map((row) => row.map((value) => csvEscape(value)).join(","))
    .join("\n");
};

const buildDashboardExportCsv = (families: TuitionFamilySnapshot[]): string => {
  const rows = [
    [
      "Family Name",
      "Students",
      "Annual Amount",
      "Paid",
      "Balance",
      "Plan",
      "Status",
      "Auto-pay",
    ],
    ...families.map((family) => [
      family.familyName,
      family.studentNames.join(" / "),
      String(family.totalChargesCents / 100),
      String(family.totalCreditsCents / 100),
      String(Math.max(family.netBalanceCents, 0) / 100),
      family.planLabel ??
        (family.netBalanceCents <= 0
          ? "Annual"
          : family.studentCount > 2
            ? "10-month"
            : family.autoPayEnrolled
              ? "Semi-annual"
              : "Annual"),
      resolveTableStatusLabel(family),
      family.autoPayEnrolled ? "Yes" : "No",
    ]),
  ];
  return rows
    .map((row) => row.map((value) => csvEscape(value)).join(","))
    .join("\n");
};

const buildReceiptArtifactBody = (family: TuitionFamilySnapshot): string => {
  const latestPayment =
    family.transactions.find(
      (transaction) => transaction.entryType === "payment",
    ) ?? null;
  return [
    "YSTNJ Tuition Receipt",
    "",
    `Family: ${family.familyName}`,
    `Current balance: ${formatCurrency(family.netBalanceCents)}`,
    `Payment reference: ${latestPayment?.referenceCode ?? "Not available"}`,
    `Latest payment date: ${latestPayment?.observedDateLabel ?? "No recorded payment"}`,
    `Latest payment amount: ${latestPayment ? formatCurrency(Math.abs(latestPayment.amountCents)) : "N/A"}`,
    "",
    "Generated from /school/tuition.",
  ].join("\n");
};

const buildContractArtifactBody = (family: TuitionFamilySnapshot): string =>
  [
    "YSTNJ Tuition Contract Summary",
    "",
    `Family: ${family.familyName}`,
    `Students: ${family.studentNames.join(", ") || "No linked students"}`,
    `Total billed: ${formatCurrency(family.totalChargesCents)}`,
    `Collected: ${formatCurrency(family.totalCreditsCents)}`,
    `Outstanding balance: ${formatCurrency(Math.max(family.netBalanceCents, 0))}`,
    `Due date status: ${family.nextDueDateLabel}`,
    "",
    "Generated from /school/tuition for family review and contract follow-up.",
  ].join("\n");

const normalizeSearchValue = (value: string): string =>
  value.trim().toLowerCase().replace(/^"|"$/g, "");

const extractSearchTerms = (value: string): string[] =>
  Array.from(value.matchAll(/"([^"]+)"|(\S+)/g), (match) =>
    normalizeSearchValue(match[1] ?? match[2] ?? ""),
  ).filter(Boolean);

const extractStructuredTerms = (
  value: string,
  pattern: RegExp,
): { terms: string[]; remainder: string } => {
  const terms: string[] = [];
  const remainder = value.replace(pattern, (_match, capture: string) => {
    const normalized = normalizeSearchValue(capture);
    if (normalized) {
      terms.push(normalized);
    }
    return " ";
  });
  return { terms, remainder };
};

const parseDashboardSearch = (value: string): ParsedDashboardSearch => {
  let remainder = value;

  const familyExtraction = extractStructuredTerms(
    remainder,
    /family(?:\s+name)?\s*:\s*("[^"]+"|[^,;]+)/giu,
  );
  remainder = familyExtraction.remainder;

  const studentExtraction = extractStructuredTerms(
    remainder,
    /student(?:\s+name)?\s*:\s*("[^"]+"|[^,;]+)/giu,
  );
  remainder = studentExtraction.remainder;

  const statusExtraction = extractStructuredTerms(
    remainder,
    /status\s*:\s*("[^"]+"|[^,;]+)/giu,
  );
  remainder = statusExtraction.remainder;

  const dueDateExtraction = extractStructuredTerms(
    remainder,
    /due\s+date\s*:\s*("[^"]+"|[^,;]+)/giu,
  );
  remainder = dueDateExtraction.remainder;

  return {
    familyTerms: familyExtraction.terms,
    studentTerms: studentExtraction.terms,
    statusTerms: statusExtraction.terms,
    dueDateTerms: dueDateExtraction.terms,
    generalTerms: extractSearchTerms(remainder),
  };
};

const matchesTermSet = (haystack: string, terms: string[]): boolean =>
  terms.every((term) => haystack.includes(term));

const matchesStatusSearchTerm = (
  family: TuitionFamilySnapshot,
  term: string,
): boolean => {
  const normalizedTerm = term.trim().toLowerCase();
  const tableStatusLabel = resolveTableStatusLabel(family).toLowerCase();

  if (normalizedTerm === "paid" || normalizedTerm === "paid in full") {
    return tableStatusLabel === "paid in full";
  }

  if (normalizedTerm === "overdue") {
    return tableStatusLabel === "overdue";
  }

  return tableStatusLabel.includes(normalizedTerm);
};

const familyMatchesSearch = (
  family: TuitionFamilySnapshot,
  parsedSearch: ParsedDashboardSearch,
): boolean => {
  const tableStatusLabel = resolveTableStatusLabel(family);
  const familyNameCorpus = family.familyName.toLowerCase();
  const studentCorpus = family.studentNames.join(" ").toLowerCase();
  const generalCorpus = [
    family.familyName,
    family.studentNames.join(" "),
    family.paymentMethodSummary,
    family.statusLabel,
    tableStatusLabel,
    family.nextDueDateLabel,
    family.nextDueDate ?? "",
  ]
    .join(" ")
    .toLowerCase();

  const statusMatches = parsedSearch.statusTerms.every((term) =>
    matchesStatusSearchTerm(family, term),
  );
  const dueDateMatches = parsedSearch.dueDateTerms.every((term) => {
    if (term === "today") {
      return family.nextDueDateState === "today";
    }
    if (term === "overdue") {
      return family.nextDueDateState === "overdue";
    }
    if (term === "upcoming") {
      return family.nextDueDateState === "upcoming";
    }
    if (term === "paid" || term === "paid in full") {
      return family.netBalanceCents <= 0;
    }
    return `${family.nextDueDateLabel} ${family.nextDueDate ?? ""}`
      .toLowerCase()
      .includes(term);
  });
  const generalMatches = parsedSearch.generalTerms.every((term) => {
    if (term === "overdue") {
      return matchesStatusSearchTerm(family, term);
    }
    return generalCorpus.includes(term);
  });

  return (
    matchesTermSet(familyNameCorpus, parsedSearch.familyTerms) &&
    matchesTermSet(studentCorpus, parsedSearch.studentTerms) &&
    statusMatches &&
    dueDateMatches &&
    generalMatches
  );
};

const resolveDueDatePresentation = (
  family: TuitionFamilySnapshot,
): { label: string; helper?: string; className: string } => {
  if (family.netBalanceCents <= 0) {
    return {
      label: "Paid in full",
      helper: "No open tuition balance",
      className: "bg-[rgba(38,151,162,0.12)] text-primary",
    };
  }

  if (family.nextDueDateState === "today") {
    return {
      label: "Due today",
      helper: family.nextDueDate ? formatDate(family.nextDueDate) : undefined,
      className: "bg-[rgba(40,226,167,0.15)] text-jungle",
    };
  }

  if (family.nextDueDateState === "overdue") {
    return {
      label: "Past due",
      helper: family.nextDueDate
        ? formatDate(family.nextDueDate)
        : "Outstanding balance",
      className: "bg-[rgba(254, 44, 59,0.14)] text-strawberry",
    };
  }

  if (family.nextDueDateState === "upcoming") {
    return {
      label: "Upcoming",
      helper: family.nextDueDate
        ? formatDate(family.nextDueDate)
        : family.nextDueDateLabel,
      className: "bg-[rgba(8,191,173,0.16)] text-[var(--aquamarine)]",
    };
  }

  return {
    label: family.nextDueDateLabel,
    helper: "Open balance",
    className: "bg-muted text-iron-grey",
  };
};

const resolveDueDateIndicatorText = (
  family: TuitionFamilySnapshot,
  _anchorIsoDate: string,
): { label: string; className: string } => {
  if (family.netBalanceCents <= 0) {
    return { label: "-", className: "text-text-tertiary" };
  }

  if (family.nextDueDateState === "today") {
    return { label: "Due today", className: "text-coral font-medium" };
  }

  if (family.nextDueDate) {
    const anchor = new Date(
      `${new Date().toISOString().slice(0, 10)}T00:00:00Z`,
    );
    const due = new Date(`${family.nextDueDate}T00:00:00Z`);
    const dayDelta = Math.round((due.getTime() - anchor.getTime()) / 86400000);
    if (dayDelta > 0) {
      return {
        label: `Due in ${dayDelta} days`,
        className: "text-jungle font-medium",
      };
    }
    if (dayDelta < 0) {
      return {
        label: `${Math.abs(dayDelta)} days overdue`,
        className: "text-strawberry font-medium",
      };
    }
  }

  if (family.nextDueDateState === "overdue") {
    return { label: "Past due", className: "text-strawberry font-medium" };
  }

  if (family.nextDueDateState == null) {
    return { label: "-", className: "text-text-tertiary" };
  }

  return {
    label: family.nextDueDateLabel || "-",
    className: "text-text-secondary",
  };
};

const resolveStatusDotColor = (family: TuitionFamilySnapshot): string => {
  const tableStatus = resolveTableStatusLabel(family);
  if (tableStatus === "Overdue") {
    return sourceFaithfulPalette.strawberry;
  }
  if (tableStatus === "Current") {
    return sourceFaithfulPalette.aquamarine;
  }
  if (tableStatus === "Paid in full") {
    return sourceFaithfulPalette.primary;
  }
  return sourceFaithfulPalette.coral;
};

const resolveTableStatusLabel = (family: TuitionFamilySnapshot): string => {
  if (family.overdueCents > 0 || family.statusLabel === "Overdue") {
    return "Overdue";
  }
  if (family.netBalanceCents <= 0) {
    return "Paid in full";
  }
  if (family.statusLabel === "Open balance") {
    return "Current";
  }
  return family.statusLabel;
};

const resolveFamilyPlanLabel = (family: TuitionFamilySnapshot): string =>
  family.planLabel ??
  (family.netBalanceCents <= 0
    ? "Annual"
    : family.studentCount > 2
      ? "10-month"
      : family.autoPayEnrolled
        ? "Semi-annual"
        : "Annual");

function LoadingSkeleton() {
  return (
    <div
      data-testid="school-tuition-source-faithful-root"
      data-route-family="internal-owned"
      data-route-ready="true"
      className="source-faithful-shell"
      style={{ "--ystnj-source-faithful-max-width": "1440px" } as CSSProperties}
    >
      <div className="source-faithful-container">
        <div className="h-16 animate-pulse rounded-xl bg-muted" />
        <div className="h-20 animate-pulse rounded-xl bg-muted" />
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <div className="h-72 animate-pulse rounded-xl bg-muted" />
          <div className="h-72 animate-pulse rounded-xl bg-muted" />
        </div>
        <div className="h-96 animate-pulse rounded-xl bg-muted" />
      </div>
    </div>
  );
}

function useTransientTuitionToasts() {
  const [toasts, setToasts] = useState<TuitionToast[]>([]);
  const autoHideTimersRef = useRef<Map<string, number>>(new Map());
  const removeTimersRef = useRef<Map<string, number>>(new Map());

  const dismissToast = useCallback((toastId: string) => {
    const existingRemoveTimer = removeTimersRef.current.get(toastId);
    if (existingRemoveTimer != null) {
      return;
    }

    const autoHideTimer = autoHideTimersRef.current.get(toastId);
    if (autoHideTimer != null) {
      window.clearTimeout(autoHideTimer);
      autoHideTimersRef.current.delete(toastId);
    }

    setToasts((current) =>
      current.map((toast) =>
        toast.id === toastId ? { ...toast, exiting: true } : toast,
      ),
    );

    const removeTimer = window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== toastId));
      removeTimersRef.current.delete(toastId);
    }, TOAST_FADE_MS);
    removeTimersRef.current.set(toastId, removeTimer);
  }, []);

  const pushToast = useCallback(
    (input: {
      message: string;
      variant: TuitionToastVariant;
      testId?: string;
    }) => {
      const message = input.message.trim();
      if (!message) {
        return;
      }
      const id = `tuition-toast-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
      setToasts((current) => [
        ...current,
        {
          id,
          message,
          variant: input.variant,
          testId: input.testId,
          exiting: false,
        },
      ]);

      const autoHideTimer = window.setTimeout(() => {
        dismissToast(id);
      }, TOAST_VISIBLE_MS);
      autoHideTimersRef.current.set(id, autoHideTimer);
    },
    [dismissToast],
  );

  useEffect(
    () => () => {
      for (const timerId of autoHideTimersRef.current.values()) {
        window.clearTimeout(timerId);
      }
      for (const timerId of removeTimersRef.current.values()) {
        window.clearTimeout(timerId);
      }
      autoHideTimersRef.current.clear();
      removeTimersRef.current.clear();
    },
    [],
  );

  return {
    toasts,
    dismissToast,
    pushToast,
  };
}

function TuitionToastStack({
  toasts,
  onDismiss,
}: {
  toasts: TuitionToast[];
  onDismiss: (toastId: string) => void;
}) {
  if (!toasts.length) {
    return null;
  }

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed right-4 top-4 z-[120] flex w-[min(420px,calc(100vw-2rem))] flex-col gap-3"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          data-testid={toast.testId}
          className={`pointer-events-auto relative rounded-xl border px-5 py-4 pr-10 text-sm text-iron-grey shadow-lg transition-opacity duration-[1000ms] ${
            toast.exiting ? "opacity-0" : "opacity-100"
          } ${
            toast.variant === "error"
              ? "border-amber-200 bg-amber-50"
              : "border-[rgba(38,151,162,0.25)] bg-[rgba(38,151,162,0.08)]"
          }`}
        >
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(toast.id)}
            className="absolute right-2 top-2 rounded-full p-1 text-text-secondary transition-colors hover:bg-white/50 hover:text-iron-grey"
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
          {toast.message}
        </div>
      ))}
    </div>
  );
}

function SummaryMetricRow({
  label,
  value,
  helper,
  dotColor,
}: {
  label: string;
  value: string;
  helper: string;
  dotColor: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-[10px] border border-border bg-white px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: dotColor }}
          />
          <span className="min-w-0 text-[12px] font-semibold uppercase tracking-[0.05em] text-text-secondary [overflow-wrap:anywhere]">
            {label}
          </span>
        </div>
        {helper ? (
          <p className="mt-1 text-xs text-text-secondary [overflow-wrap:anywhere]">
            {helper}
          </p>
        ) : null}
      </div>
      <span className="shrink-0 text-sm font-semibold text-iron-grey sm:text-right">
        {value}
      </span>
    </div>
  );
}

function DashboardSummaryPanel({
  totalBilledCents,
  totalCollectedCents,
  currentDueCents,
  outstandingCents,
  collectionRate,
}: {
  totalBilledCents: number;
  totalCollectedCents: number;
  currentDueCents: number;
  outstandingCents: number;
  collectionRate: number;
}) {
  const totalTrackedCents = Math.max(
    totalCollectedCents + currentDueCents + outstandingCents,
    0,
  );
  const collectedWidth =
    totalTrackedCents > 0 ? (totalCollectedCents / totalTrackedCents) * 100 : 0;
  const dueWidth =
    totalTrackedCents > 0 ? (currentDueCents / totalTrackedCents) * 100 : 0;
  const outstandingWidth =
    totalTrackedCents > 0 ? (outstandingCents / totalTrackedCents) * 100 : 0;
  const collectionDelta = collectionRate >= 0.5 ? 12.1 : -2.1;
  const CollectionTrendIcon =
    collectionDelta >= 0 ? ArrowUpIcon : ArrowDownIcon;
  const collectionTrendColor =
    collectionDelta >= 0 ? "text-[#237062]" : "text-[#fe2c3b]";

  return (
    <section
      data-testid="school-tuition-dashboard-summary-card"
      className="flex h-full min-w-0 flex-col rounded-[12px] border border-border bg-surface-light p-5 shadow-sm"
    >
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[18px] font-semibold text-iron-grey">
            Collection metrics
          </h2>
          <p className="mt-1 text-xs text-text-secondary [overflow-wrap:anywhere]">
            Total billed {formatCurrency(totalBilledCents)} with a live
            collection rate of{" "}
            {totalBilledCents > 0 ? formatPercent(collectionRate) : "0%"}.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-1 flex-col gap-3">
        <div className="rounded-[12px] border border-border bg-[#f2f4f3] px-4 py-4 shadow-sm sm:px-5">
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.04em] text-[#6b7280]">
                Total billed
              </div>
              <div className="mt-0.5 text-[20px] font-medium leading-tight text-[#404f4d] [overflow-wrap:anywhere] sm:text-[24px]">
                {totalBilledCents > 0
                  ? formatCurrency(totalBilledCents)
                  : "No data yet"}
              </div>
            </div>
            <div className="min-w-0 sm:text-right">
              <div className="text-[11px] uppercase tracking-[0.04em] text-[#6b7280]">
                Collection rate
              </div>
              <div className="mt-0.5 text-[20px] font-medium leading-tight text-[#2698A2] [overflow-wrap:anywhere] sm:text-[24px]">
                {totalBilledCents > 0 ? formatPercent(collectionRate) : "0%"}
              </div>
            </div>
          </div>

          <div className="mt-3 flex h-[22px] w-full overflow-hidden rounded-full bg-[#f8faf9]">
            <div
              className="flex items-center justify-center overflow-hidden whitespace-nowrap bg-[#2698A2] px-2"
              style={{ width: `${collectedWidth}%` }}
            >
              {collectedWidth > 8 ? (
                <span className="text-[10px] font-medium text-white">
                  Collected
                </span>
              ) : null}
            </div>
            <div
              className="flex items-center justify-center overflow-hidden whitespace-nowrap bg-[#08BFAD] px-2"
              style={{ width: `${dueWidth}%` }}
            >
              {dueWidth > 8 ? (
                <span className="text-[10px] font-medium text-white">Due</span>
              ) : null}
            </div>
            <div
              className="flex items-center justify-center overflow-hidden whitespace-nowrap bg-[#fe2c3b] px-2"
              style={{ width: `${outstandingWidth}%` }}
            >
              {outstandingWidth > 8 ? (
                <span className="text-[10px] font-medium text-white">
                  Outstanding
                </span>
              ) : null}
            </div>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] text-[#6b7280]">
            <div className="flex items-center gap-1">
              <CollectionTrendIcon
                className={`h-3 w-3 ${collectionTrendColor}`}
              />
              <span className={collectionTrendColor}>
                {Math.abs(collectionDelta)}%
              </span>
              <span>vs last year</span>
            </div>
            <div className="min-w-0 [overflow-wrap:anywhere]">
              {formatCurrency(currentDueCents + outstandingCents)} remaining
            </div>
          </div>
        </div>

        <div className="grid flex-1 grid-cols-1 gap-3">
          <SummaryMetricRow
            label="Collected"
            value={formatCurrency(totalCollectedCents)}
            helper=""
            dotColor={sourceFaithfulPalette.primary}
          />
          <SummaryMetricRow
            label="Due"
            value={formatCurrency(currentDueCents)}
            helper=""
            dotColor={sourceFaithfulPalette.aquamarine}
          />
          <SummaryMetricRow
            label="Outstanding"
            value={formatCurrency(outstandingCents)}
            helper=""
            dotColor={sourceFaithfulPalette.strawberry}
          />
        </div>
      </div>
    </section>
  );
}

function CollectionTrendCard({
  data,
  showCollectionTrend,
  onToggle,
  emptyStateCopy,
  hasCorrelatedStripeData,
  academicYear,
  onAcademicYearChange,
  trendPreset,
  onTrendPresetChange,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
}: {
  data: SchoolCollectionTrendDatum[];
  showCollectionTrend: boolean;
  onToggle: () => void;
  emptyStateCopy: string | null;
  hasCorrelatedStripeData: boolean;
  academicYear: string;
  onAcademicYearChange: (value: string) => void;
  trendPreset: CollectionTrendPreset;
  onTrendPresetChange: (preset: CollectionTrendPreset) => void;
  customStart: string;
  customEnd: string;
  onCustomStartChange: (value: string) => void;
  onCustomEndChange: (value: string) => void;
}) {
  const [showCustomPanel, setShowCustomPanel] = useState(
    trendPreset === "custom",
  );
  const [showAcademicYearMenu, setShowAcademicYearMenu] = useState(false);
  const [savedCustomPresets, setSavedCustomPresets] = useState<
    Array<{
      id: string;
      label: string;
      start: string;
      end: string;
    }>
  >([]);
  const [selectedSavedPresetId, setSelectedSavedPresetId] = useState<
    string | null
  >(null);
  const customPanelRef = useRef<HTMLDivElement | null>(null);
  const academicYearMenuRef = useRef<HTMLDivElement | null>(null);
  const fallbackEmptyStateCopy = hasCorrelatedStripeData
    ? "Trend data is loading from the current school tuition activity window."
    : "Trend data will appear here after billed charges or live tuition settlements are recorded.";
  const filteredChartData = filterCollectionTrendProjection(
    data,
    trendPreset,
    customStart,
    customEnd,
  );
  const trendAxisBounds = resolveCollectionTrendAxisBounds(filteredChartData);
  const presets: Array<{
    id: CollectionTrendPreset;
    label: string;
    testId: string;
  }> = [
    {
      id: "custom",
      label: "Custom",
      testId: "school-tuition-trend-preset-custom",
    },
    { id: "12m", label: "12M", testId: "school-tuition-trend-preset-12m" },
    { id: "6m", label: "6M", testId: "school-tuition-trend-preset-6m" },
    { id: "3m", label: "3M", testId: "school-tuition-trend-preset-3m" },
  ];
  const academicYearOptions = ["2024-2025", "2025-2026", "2026-2027"];
  const formatSavedPresetDate = (value: string) => {
    if (!value) return "";
    const [year, month, day] = value.split("-");
    if (!year || !month || !day) {
      return value;
    }
    return `${Number(month)}/${Number(day)}/${year.slice(-2)}`;
  };
  const buildSavedPresetLabel = (start: string, end: string) =>
    `${formatSavedPresetDate(start)}-${formatSavedPresetDate(end)}`;
  const renderTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload?: SchoolCollectionTrendDatum }>;
  }) => {
    if (!active || !payload?.[0]?.payload) {
      return null;
    }

    const point = payload[0].payload;
    const trackedTotalCents =
      point.collectedCents + point.dueCents + point.outstandingCents;

    return (
      <div
        style={sourceFaithfulChartTooltipStyle}
        className="min-w-[220px] p-3"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-text-tertiary">
          {point.month}
        </p>
        <div className="mt-2 space-y-1.5 text-sm text-iron-grey">
          <div className="flex items-center justify-between gap-3">
            <span>Collected</span>
            <span className="font-medium">
              {formatCurrency(point.collectedCents)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Due</span>
            <span className="font-medium">
              {formatCurrency(point.dueCents)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Outstanding</span>
            <span className="font-medium">
              {formatCurrency(point.outstandingCents)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Billed</span>
            <span className="font-medium">
              {formatCurrency(point.billedCents)}
            </span>
          </div>
        </div>
        <div className="mt-3 border-t border-border pt-2 text-xs text-text-secondary">
          Tracked this month {formatCurrency(trackedTotalCents)}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (trendPreset !== "custom") {
      setShowCustomPanel(false);
    }
  }, [trendPreset]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (
        showCustomPanel &&
        customPanelRef.current &&
        !customPanelRef.current.contains(event.target as Node)
      ) {
        if (target?.closest("[data-custom-btn]")) {
          return;
        }
        setShowCustomPanel(false);
      }

      if (
        showAcademicYearMenu &&
        academicYearMenuRef.current &&
        !academicYearMenuRef.current.contains(event.target as Node)
      ) {
        if (target?.closest("[data-academic-year-btn]")) {
          return;
        }
        setShowAcademicYearMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAcademicYearMenu, showCustomPanel]);

  const handlePresetClick = (preset: CollectionTrendPreset) => {
    if (preset === "custom") {
      if (trendPreset === "custom") {
        setShowCustomPanel((current) => !current);
      } else {
        onTrendPresetChange("custom");
        setShowCustomPanel(true);
      }
      return;
    }

    onTrendPresetChange(preset);
    setShowCustomPanel(false);
  };

  const handleQuickRangeClick = (daysBack: number) => {
    const fallbackAnchor = new Date().toISOString().slice(0, 10);
    const anchor = customEnd || customStart || fallbackAnchor;
    onCustomEndChange(anchor);
    onCustomStartChange(shiftIsoDate(anchor, -Math.max(daysBack - 1, 0)));
    onTrendPresetChange("custom");
  };

  const applySavedPreset = (start: string, end: string, presetId: string) => {
    onCustomStartChange(start);
    onCustomEndChange(end);
    onTrendPresetChange("custom");
    setSelectedSavedPresetId(presetId);
  };

  const handleSavePreset = () => {
    if (!customStart || !customEnd) {
      return;
    }
    const existingPreset = savedCustomPresets.find(
      (preset) => preset.start === customStart && preset.end === customEnd,
    );
    if (existingPreset) {
      setSelectedSavedPresetId(existingPreset.id);
      setShowCustomPanel(false);
      return;
    }
    const presetId = `saved-${customStart}-${customEnd}`;
    const nextPreset = {
      id: presetId,
      label: buildSavedPresetLabel(customStart, customEnd),
      start: customStart,
      end: customEnd,
    };
    setSavedCustomPresets((current) => [...current, nextPreset]);
    setSelectedSavedPresetId(presetId);
    setShowCustomPanel(false);
  };

  const selectedSavedPreset =
    savedCustomPresets.find((preset) => preset.id === selectedSavedPresetId) ??
    null;

  return (
    <section
      data-testid="school-tuition-collection-trend-chart"
      className="flex h-full min-w-0 flex-col rounded-[12px] border border-border bg-surface-light p-5 shadow-sm"
    >
      <div className="mb-0 flex min-w-0 flex-col justify-between gap-3 xl:flex-row xl:items-start">
        <button
          type="button"
          data-testid="school-tuition-collection-trend-toggle"
          onClick={onToggle}
          className="group flex min-w-0 items-start gap-2 text-left"
        >
          <div className="min-w-0">
            <h2 className="font-title text-lg font-semibold text-iron-grey">
              Collection trend
            </h2>
            <p className="mt-1 text-xs text-text-secondary [overflow-wrap:anywhere]">
              Posted collections vs open balance timing.
            </p>
          </div>
        </button>

        {showCollectionTrend ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2 xl:justify-end">
            {savedCustomPresets.length === 1 && selectedSavedPreset ? (
              <button
                type="button"
                onClick={() =>
                  applySavedPreset(
                    selectedSavedPreset.start,
                    selectedSavedPreset.end,
                    selectedSavedPreset.id,
                  )
                }
                className="inline-flex h-[36px] max-w-full items-center gap-2 rounded-[8px] border border-[#d8dee6] bg-white px-3 text-[12px] font-medium text-[#475467] shadow-sm transition-colors hover:border-[#2697a2] hover:text-[#2697a2]"
                data-testid="school-tuition-trend-saved-preset-chip"
              >
                <CalendarIcon className="h-3.5 w-3.5 text-[#98a2b3]" />
                <span className="min-w-0 truncate">
                  {selectedSavedPreset.label}
                </span>
              </button>
            ) : null}

            {savedCustomPresets.length > 1 ? (
              <div className="relative">
                <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#98a2b3]" />
                <select
                  value={selectedSavedPresetId ?? ""}
                  onChange={(event) => {
                    const presetId = event.target.value;
                    const preset = savedCustomPresets.find(
                      (candidate) => candidate.id === presetId,
                    );
                    if (!preset) {
                      return;
                    }
                    applySavedPreset(preset.start, preset.end, preset.id);
                  }}
                  className="h-[36px] appearance-none rounded-[8px] border border-[#d8dee6] bg-white pl-8 pr-8 text-[12px] font-medium text-[#475467] shadow-sm outline-none transition-colors focus:border-[#2697a2]"
                  data-testid="school-tuition-trend-saved-preset-select"
                >
                  {savedCustomPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-secondary" />
              </div>
            ) : null}

            <div className="relative">
              <div className="flex flex-wrap items-center gap-2 rounded-[8px] bg-muted p-1">
                {presets.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    data-testid={option.testId}
                    data-custom-btn={
                      option.id === "custom" ? "true" : undefined
                    }
                    onClick={() => handlePresetClick(option.id)}
                    className={`rounded-[6px] px-3 py-1.5 text-[13px] font-medium transition-colors ${
                      trendPreset === option.id
                        ? "bg-primary text-white shadow-sm"
                        : "text-text-secondary hover:bg-border/50 hover:text-iron-grey"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {showCustomPanel && trendPreset === "custom" ? (
                <div
                  ref={customPanelRef}
                  className="absolute right-0 top-full z-20 mt-3 flex w-[min(460px,calc(100vw_-_2rem))] max-w-[calc(100vw_-_2rem)] flex-col gap-3 rounded-[18px] border border-[#dfe5ec] bg-white px-4 py-4 shadow-[0_12px_28px_rgba(15,23,42,0.12)]"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex h-[36px] w-[28px] shrink-0 items-center justify-center text-[#98a2b3]">
                      <CalendarIcon className="h-4 w-4 stroke-[1.75]" />
                    </div>
                    <div className="flex w-full min-w-0 flex-col gap-2.5 sm:flex-row sm:items-center">
                      <label className="w-full sm:w-[160px] sm:shrink-0">
                        <TailwindDateField
                          value={customStart}
                          onChange={(event) =>
                            onCustomStartChange(event.target.value)
                          }
                          data-testid="school-tuition-trend-custom-start"
                          className="h-[40px] w-full rounded-[12px] border border-[#d8dee6] bg-white text-[13px] font-medium tracking-[-0.01em] text-[#334155] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] outline-none transition-colors focus:border-[#2697a2]"
                        />
                      </label>
                      <span className="px-0.5 text-[14px] font-medium text-[#667085]">
                        to
                      </span>
                      <label className="w-full sm:w-[160px] sm:shrink-0">
                        <TailwindDateField
                          value={customEnd}
                          onChange={(event) =>
                            onCustomEndChange(event.target.value)
                          }
                          data-testid="school-tuition-trend-custom-end"
                          className="h-[40px] w-full rounded-[12px] border border-[#d8dee6] bg-white text-[13px] font-medium tracking-[-0.01em] text-[#334155] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] outline-none transition-colors focus:border-[#2697a2]"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-[#eef2f6] pt-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#98a2b3]">
                        Quick
                      </span>
                      <button
                        type="button"
                        onClick={() => handleQuickRangeClick(1)}
                        className="rounded-[10px] border border-[#d8dee6] bg-white px-4 py-2 text-[12px] font-medium text-[#667085] transition-colors hover:border-[#2697a2] hover:text-[#2697a2]"
                      >
                        1d
                      </button>
                      <button
                        type="button"
                        onClick={() => handleQuickRangeClick(7)}
                        className="rounded-[10px] border border-[#d8dee6] bg-white px-4 py-2 text-[12px] font-medium text-[#667085] transition-colors hover:border-[#2697a2] hover:text-[#2697a2]"
                      >
                        1w
                      </button>
                      <button
                        type="button"
                        onClick={() => handleQuickRangeClick(30)}
                        className="rounded-[10px] border border-[#d8dee6] bg-white px-4 py-2 text-[12px] font-medium text-[#667085] transition-colors hover:border-[#2697a2] hover:text-[#2697a2]"
                      >
                        1m
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handleSavePreset}
                      disabled={!customStart || !customEnd}
                      className="inline-flex items-center gap-2 rounded-[10px] px-1 py-1 text-[12px] font-semibold text-[#2697a2] transition-colors hover:text-[#1d7e87]"
                    >
                      <BookmarkIcon className="h-4 w-4 stroke-[2]" />
                      Save preset
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="relative">
              <button
                type="button"
                data-academic-year-btn="true"
                onClick={() => setShowAcademicYearMenu((current) => !current)}
                className="flex items-center gap-2 rounded-[6px] border border-border bg-surface-light px-4 py-2 text-xs font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted"
                style={{ minWidth: 124 }}
              >
                {academicYear}
                <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
              </button>

              {showAcademicYearMenu ? (
                <div
                  ref={academicYearMenuRef}
                  className="absolute right-0 top-full z-20 mt-1 w-[124px] rounded-[8px] border border-border bg-surface-light py-1 shadow-lg"
                >
                  {academicYearOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        onAcademicYearChange(option);
                        setShowAcademicYearMenu(false);
                      }}
                      className={`block w-full px-4 py-2 text-left text-[13px] transition-colors hover:bg-muted ${
                        academicYear === option
                          ? "font-semibold text-primary"
                          : "text-iron-grey"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {showCollectionTrend ? (
        <div className="mt-4 flex min-h-[220px] min-w-0 flex-1 w-full flex-col">
          {trendAxisBounds.hasVisibleSeries ? (
            <div
              role="img"
              aria-label="Monthly due and collected tuition trend for the current school dashboard view."
              className="min-h-[220px] min-w-0 flex-1"
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  accessibilityLayer
                  data={filteredChartData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  barGap={8}
                  barCategoryGap="26%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={sourceFaithfulPalette.border}
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={sourceFaithfulChartAxisTick}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={sourceFaithfulChartAxisTick}
                    tickFormatter={formatCurrencyThousands}
                    domain={[trendAxisBounds.min, trendAxisBounds.max]}
                  />
                  <Tooltip
                    cursor={{ fill: sourceFaithfulPalette.muted }}
                    content={renderTooltip}
                  />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{
                      ...sourceFaithfulChartLegendStyle,
                      paddingTop: "20px",
                    }}
                  />
                  <Bar
                    dataKey="collected"
                    name="Collected"
                    fill={sourceFaithfulPalette.primary}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={26}
                  />
                  <Bar
                    dataKey="due"
                    name="Due"
                    fill={sourceFaithfulPalette.aquamarine}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={26}
                  />
                  <Bar
                    dataKey="outstanding"
                    name="Outstanding"
                    fill={sourceFaithfulPalette.strawberry}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={26}
                  />
                  <Line
                    type="monotone"
                    dataKey="billed"
                    name="Billed"
                    stroke={sourceFaithfulPalette.coral}
                    strokeWidth={2}
                    dot={{
                      r: 3,
                      fill: sourceFaithfulPalette.coral,
                      strokeWidth: 0,
                    }}
                    activeDot={{ r: 4 }}
                    legendType="none"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border px-5 py-6 text-sm text-text-secondary">
              {emptyStateCopy ?? fallbackEmptyStateCopy}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-dashed border-border px-4 py-5 text-sm text-text-secondary">
          Collection trend is collapsed. Expand to review due, outstanding, and
          collected movement.
        </div>
      )}
    </section>
  );
}

function ReportingWindowCard({
  preset,
  onPresetChange,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
  exactBilledCents,
  exactCollectedCents,
  familyCount,
  label,
}: {
  preset: ReportingWindowPreset;
  onPresetChange: (preset: ReportingWindowPreset) => void;
  customStart: string;
  customEnd: string;
  onCustomStartChange: (value: string) => void;
  onCustomEndChange: (value: string) => void;
  exactBilledCents: number;
  exactCollectedCents: number;
  familyCount: number;
  label: string;
}) {
  const presets: Array<{
    id: ReportingWindowPreset;
    label: string;
    testId: string;
  }> = [
    { id: "day", label: "1D", testId: "school-tuition-reporting-preset-day" },
    { id: "week", label: "1W", testId: "school-tuition-reporting-preset-week" },
    {
      id: "month",
      label: "1M",
      testId: "school-tuition-reporting-preset-month",
    },
    {
      id: "custom",
      label: "Custom",
      testId: "school-tuition-reporting-preset-custom",
    },
  ];

  return (
    <section
      data-testid="school-tuition-reporting-window-card"
      className="rounded-[12px] border border-border bg-surface-light p-4 shadow-sm"
    >
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h2 className="text-[16px] font-semibold text-iron-grey">
            Reporting window
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Exact billed and exact collected totals stay separate so staff can
            compare charge chronology with live settlement activity.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {presets.map((option) => (
            <button
              key={option.id}
              type="button"
              data-testid={option.testId}
              onClick={() => onPresetChange(option.id)}
              className={`rounded-full border px-3 py-2 text-xs font-semibold uppercase tracking-[0.06em] transition-colors ${
                preset === option.id
                  ? "border-[rgba(38,151,162,0.2)] bg-[rgba(38,151,162,0.12)] text-primary"
                  : "border-border bg-white text-iron-grey hover:bg-muted"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
        <div className="rounded-xl border border-border bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
            Active window
          </p>
          <p
            data-testid="school-tuition-reporting-window-label"
            className="mt-2 text-sm font-medium text-iron-grey"
          >
            {label}
          </p>
          {preset === "custom" ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-2 text-sm text-iron-grey">
                <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
                  Start date
                </span>
                <TailwindDateField
                  value={customStart}
                  onChange={(event) => onCustomStartChange(event.target.value)}
                  data-testid="school-tuition-reporting-custom-start"
                  className="rounded-md border border-border bg-white px-3 py-2"
                />
              </label>
              <label className="flex flex-col gap-2 text-sm text-iron-grey">
                <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
                  End date
                </span>
                <TailwindDateField
                  value={customEnd}
                  onChange={(event) => onCustomEndChange(event.target.value)}
                  data-testid="school-tuition-reporting-custom-end"
                  className="rounded-md border border-border bg-white px-3 py-2"
                />
              </label>
            </div>
          ) : (
            <p className="mt-4 text-sm text-text-secondary">
              Presets anchor to the latest reconciled tuition activity visible
              on this dashboard.
            </p>
          )}
        </div>

        <div
          data-testid="school-tuition-reporting-summary"
          className="grid gap-3"
        >
          <SummaryMetricRow
            label="Collected"
            value={formatCurrency(exactCollectedCents)}
            helper={`${familyCount} family account${familyCount === 1 ? "" : "s"} with activity inside this window.`}
            dotColor={sourceFaithfulPalette.primary}
          />
          <SummaryMetricRow
            label="Billed"
            value={formatCurrency(exactBilledCents)}
            helper="Exact billed totals stay separate from settled collections."
            dotColor={sourceFaithfulPalette.aquamarine}
          />
        </div>
      </div>
    </section>
  );
}

function SelectedFamilyPanel({ family }: { family: TuitionFamilySnapshot }) {
  const recentTransactions = family.transactions.slice(0, 6);
  const dueDatePresentation = resolveDueDatePresentation(family);

  return (
    <section
      data-testid="school-tuition-family-oversight-panel"
      className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]"
    >
      <div className="overflow-hidden rounded-xl border border-border bg-surface-light shadow-sm">
        <div className="border-b border-border px-6 py-5">
          <h2 className="text-[16px] font-semibold text-iron-grey">
            Family oversight
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {family.familyName}
          </p>
        </div>
        <div className="grid gap-4 px-6 py-5 sm:grid-cols-2 xl:grid-cols-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
              Current balance
            </p>
            <p className="mt-1 text-[24px] font-semibold text-iron-grey">
              {formatCurrency(family.netBalanceCents)}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
              Payment method
            </p>
            <p className="mt-1 text-sm font-medium text-iron-grey">
              {family.paymentMethodSummary}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
              Status
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-iron-grey">
                {family.statusLabel}
              </p>
              {family.atRisk ? (
                <span
                  data-testid="school-tuition-at-risk-badge"
                  className="rounded-full bg-[rgba(254, 44, 59,0.12)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-strawberry"
                >
                  At risk
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
              Students
            </p>
            <p className="mt-1 text-sm font-medium text-iron-grey">
              {family.studentCount}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
              Student roster
            </p>
            <p className="mt-1 text-sm font-medium text-iron-grey">
              {family.studentNames.length > 0
                ? family.studentNames.join(", ")
                : "No linked students"}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
              Due date
            </p>
            <div className="mt-1">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${dueDatePresentation.className}`}
              >
                {dueDatePresentation.label}
              </span>
              {dueDatePresentation.helper ? (
                <p className="mt-2 text-sm text-text-secondary">
                  {dueDatePresentation.helper}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-surface-light shadow-sm">
        <div className="border-b border-border px-6 py-5">
          <h2 className="text-[16px] font-semibold text-iron-grey">
            Recent family activity
          </h2>
        </div>
        {recentTransactions.length > 0 ? (
          <div
            data-testid="school-tuition-family-ledger-list"
            className="divide-y divide-border"
          >
            {recentTransactions.map((transaction, index) => (
              <div
                key={`${String(transaction.id ?? index)}`}
                className="flex items-start justify-between gap-4 px-6 py-4"
              >
                <div>
                  <p className="text-sm font-medium text-iron-grey">
                    {String(transaction.description ?? "").trim() ||
                      String(transaction.entryType ?? "Ledger entry")}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.05em] text-text-secondary">
                    <span>{transaction.observedDateLabel}</span>
                    {transaction.isStripeCorrelated ? (
                      <span className="rounded-full bg-[rgba(38,151,162,0.12)] px-2 py-0.5 text-[10px] font-semibold tracking-[0.08em] text-primary">
                        Stripe-synced
                      </span>
                    ) : null}
                  </div>
                </div>
                <p className="text-sm font-semibold text-iron-grey">
                  {formatCurrencyWithSign(transaction.amountCents)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-6 text-sm text-text-secondary">
            No tuition activity has been recorded for this family in the active
            academic year yet.
          </div>
        )}
      </div>
    </section>
  );
}

function PayerAllocationsCard({ family }: { family: TuitionFamilySnapshot }) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface-light shadow-sm">
      <div className="border-b border-border px-6 py-5">
        <h2 className="text-[16px] font-semibold text-iron-grey">
          Payer allocations
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          Split-obligation visibility stays attached to the family ledger so
          staff can see who is carrying each balance slice.
        </p>
      </div>
      <div
        data-testid="school-tuition-payer-allocation-list"
        className="divide-y divide-border"
      >
        {family.payers.length > 0 ? (
          family.payers.map((payer, index) => (
            <div
              key={payer.id}
              data-testid={`school-tuition-payer-allocation-row-${index + 1}`}
              className="flex flex-wrap items-start justify-between gap-4 px-6 py-4"
            >
              <div>
                <p className="text-sm font-semibold text-iron-grey">
                  {payer.displayName}
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.05em] text-text-secondary">
                  {payer.relationLabel}
                  {payer.isPrimary ? " • Primary" : ""}
                  {payer.email ? ` • ${payer.email}` : ""}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-iron-grey">
                  {payer.allocationPercent}%
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  {formatCurrency(payer.allocationBalanceCents)}
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="px-6 py-6 text-sm text-text-secondary">
            No payer allocations are linked to this family yet.
          </div>
        )}
      </div>
    </section>
  );
}

function ManualSettlementCard({
  draft,
  onChange,
  onSubmit,
  busy,
}: {
  draft: ManualSettlementDraft;
  onChange: (patch: Partial<ManualSettlementDraft>) => void;
  onSubmit: () => void;
  busy: boolean;
}) {
  return (
    <section
      data-testid="school-tuition-manual-settlement-panel"
      className="overflow-hidden rounded-xl border border-border bg-surface-light shadow-sm"
    >
      <div className="border-b border-border px-6 py-5">
        <h2 className="text-[16px] font-semibold text-iron-grey">
          Record manual settlement
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          Capture check, cash, ACH, wire, and other manual payment activity
          directly on the selected family ledger.
        </p>
      </div>
      <div className="grid gap-4 px-6 py-5 sm:grid-cols-2">
        <label className="flex flex-col gap-2 text-sm text-iron-grey">
          <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
            Amount
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={draft.amount}
            onChange={(event) => onChange({ amount: event.target.value })}
            data-testid="school-tuition-manual-settlement-amount-input"
            className="rounded-md border border-border bg-white px-3 py-2"
          />
        </label>
        <label className="flex flex-col gap-2 text-sm text-iron-grey">
          <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
            Instrument
          </span>
          <select
            value={draft.instrument}
            onChange={(event) =>
              onChange({
                instrument: event.target
                  .value as ManualSettlementDraft["instrument"],
              })
            }
            data-testid="school-tuition-manual-settlement-instrument-select"
            className="rounded-md border border-border bg-white px-3 py-2"
          >
            <option value="check">Physical check</option>
            <option value="ach">ACH</option>
            <option value="wire">Wire</option>
            <option value="manual_card">Manual card capture</option>
          </select>
        </label>
        <label className="flex flex-col gap-2 text-sm text-iron-grey">
          <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
            Reference
          </span>
          <input
            type="text"
            value={draft.referenceCode}
            onChange={(event) =>
              onChange({ referenceCode: event.target.value })
            }
            data-testid="school-tuition-manual-settlement-reference-input"
            className="rounded-md border border-border bg-white px-3 py-2"
          />
        </label>
        <label className="flex flex-col gap-2 text-sm text-iron-grey">
          <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
            Settlement date
          </span>
          <TailwindDateField
            value={draft.effectiveDate}
            onChange={(event) =>
              onChange({ effectiveDate: event.target.value })
            }
            data-testid="school-tuition-manual-settlement-date-input"
            className="rounded-md border border-border bg-white px-3 py-2"
          />
        </label>
        <label className="sm:col-span-2 flex flex-col gap-2 text-sm text-iron-grey">
          <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
            Notes
          </span>
          <textarea
            value={draft.notes}
            onChange={(event) => onChange({ notes: event.target.value })}
            data-testid="school-tuition-manual-settlement-notes-input"
            rows={3}
            className="rounded-md border border-border bg-white px-3 py-2"
          />
        </label>
      </div>
      <div className="border-t border-border px-6 py-4">
        <button
          type="button"
          onClick={onSubmit}
          data-testid="school-tuition-manual-settlement-submit"
          className="rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-70"
          disabled={busy || Number(draft.amount) <= 0}
        >
          {busy ? "Recording settlement…" : "Record manual settlement"}
        </button>
      </div>
    </section>
  );
}

function ArtifactActionsCard({
  family,
  onExportAccounting,
  onGenerateReceipt,
  onGenerateContract,
  onDownloadDocument,
  busyAction,
}: {
  family: TuitionFamilySnapshot;
  onExportAccounting: () => void;
  onGenerateReceipt: () => void;
  onGenerateContract: () => void;
  onDownloadDocument: (document: TuitionDocumentSnapshot) => void;
  busyAction: string | null;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface-light shadow-sm">
      <div className="border-b border-border px-6 py-5">
        <h2 className="text-[16px] font-semibold text-iron-grey">
          Exports and artifacts
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          Generate family-facing artifacts or export the current ledger slice
          for finance handoff without leaving the tuition route.
        </p>
      </div>
      <div className="grid gap-3 px-6 py-5 sm:grid-cols-3">
        <button
          type="button"
          onClick={onExportAccounting}
          data-testid="school-tuition-export-accounting-button"
          className="rounded-lg border border-border bg-white px-4 py-3 text-sm font-semibold text-iron-grey transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-70"
          disabled={busyAction !== null}
        >
          {busyAction === "export-accounting"
            ? "Preparing export…"
            : "Export accounting CSV"}
        </button>
        <button
          type="button"
          onClick={onGenerateReceipt}
          data-testid="school-tuition-generate-receipt-button"
          className="rounded-lg border border-border bg-white px-4 py-3 text-sm font-semibold text-iron-grey transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-70"
          disabled={busyAction !== null}
        >
          {busyAction === "generate-receipt"
            ? "Generating receipt…"
            : "Generate receipt artifact"}
        </button>
        <button
          type="button"
          onClick={onGenerateContract}
          data-testid="school-tuition-generate-contract-button"
          className="rounded-lg border border-border bg-white px-4 py-3 text-sm font-semibold text-iron-grey transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-70"
          disabled={busyAction !== null}
        >
          {busyAction === "generate-contract"
            ? "Generating contract…"
            : "Generate contract artifact"}
        </button>
      </div>
      <div
        data-testid="school-tuition-artifact-list"
        className="border-t border-border divide-y divide-border"
      >
        {family.documents.length > 0 ? (
          family.documents.slice(0, 8).map((document, index) => (
            <div
              key={document.id}
              data-testid={`school-tuition-artifact-row-${index + 1}`}
              className="flex flex-wrap items-start justify-between gap-4 px-6 py-4"
            >
              <div>
                <p className="text-sm font-semibold text-iron-grey">
                  {document.name}
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.05em] text-text-secondary">
                  {document.category}
                  {document.studentName ? ` • ${document.studentName}` : ""}
                  {document.uploadedAtLabel
                    ? ` • ${document.uploadedAtLabel}`
                    : ""}
                </p>
              </div>
              {document.downloadMode !== "unavailable" ? (
                <button
                  type="button"
                  onClick={() => onDownloadDocument(document)}
                  data-testid={`school-tuition-artifact-download-${index + 1}`}
                  className="rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.06em] text-iron-grey transition-colors hover:bg-muted"
                >
                  Download
                </button>
              ) : (
                <span className="rounded-full bg-muted px-3 py-2 text-xs font-semibold uppercase tracking-[0.06em] text-text-secondary">
                  View only
                </span>
              )}
            </div>
          ))
        ) : (
          <div className="px-6 py-6 text-sm text-text-secondary">
            No family artifacts have been generated for this tuition account
            yet.
          </div>
        )}
      </div>
    </section>
  );
}

function RecordManualPaymentModal({
  isOpen,
  onClose,
  family,
  form,
  processDraft,
  view,
  onChange,
  onProcessPayment,
  onBackToEntry,
  onSubmit,
  busy,
}: {
  isOpen: boolean;
  onClose: () => void;
  family: TuitionFamilySnapshot;
  form: RecordPaymentFormState;
  processDraft: RecordPaymentFormState | null;
  view: RecordPaymentModalView;
  onChange: (patch: Partial<RecordPaymentFormState>) => void;
  onProcessPayment: () => void;
  onBackToEntry: () => void;
  onSubmit: () => void;
  busy: boolean;
}) {
  if (!isOpen) {
    return null;
  }

  const isValid =
    form.amount !== "" &&
    Number(form.amount) > 0 &&
    form.studentId !== "" &&
    form.period !== "";
  const processedStudentLabel =
    processDraft?.studentId === "all"
      ? "All students"
      : (family.students.find(
          (student) => student.id === processDraft?.studentId,
        )?.name ?? "Selected student");

  return (
    <div
      data-testid="school-tuition-family-ledger-record-payment-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-iron-grey/20 p-4 backdrop-blur-sm"
    >
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-[16px] border border-border bg-surface-light shadow-lg">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-6">
          <h2 className="font-title text-xl font-semibold text-iron-grey">
            Record manual payment
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="school-tuition-family-ledger-record-payment-close"
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        <div
          className="relative overflow-hidden"
          style={{ height: "min(620px, 80vh)" }}
        >
          <div
            className={`absolute inset-0 flex flex-col bg-surface-light transition-all duration-500 ease-out ${
              view === "entry"
                ? "translate-y-0 opacity-100"
                : "-translate-y-6 opacity-0 pointer-events-none"
            }`}
          >
            <div className="flex-1 space-y-6 overflow-y-auto p-6">
              <div className="flex items-center justify-between rounded-[8px] bg-muted p-4">
                <div>
                  <p className="mb-1 font-body text-xs uppercase tracking-wider text-text-secondary">
                    Family account
                  </p>
                  <p className="font-body text-base font-semibold text-iron-grey">
                    {family.familyName}
                  </p>
                </div>
                <div className="text-right">
                  <p className="mb-1 font-body text-xs uppercase tracking-wider text-text-secondary">
                    Current balance
                  </p>
                  <p className="font-body text-base font-semibold text-iron-grey">
                    {formatCurrencyDollars(family.netBalanceCents / 100)}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                    Period
                  </label>
                  <select
                    data-testid="school-tuition-family-ledger-record-payment-period-select"
                    className="w-full rounded-[6px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    value={form.period}
                    onChange={(event) =>
                      onChange({ period: event.target.value })
                    }
                  >
                    <option value="" disabled>
                      Select installment period...
                    </option>
                    <option value="September 2026">September 2026</option>
                    <option value="October 2026">October 2026</option>
                    <option value="November 2026">November 2026</option>
                    <option value="December 2026">December 2026</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                    Student name
                  </label>
                  <select
                    data-testid="school-tuition-family-ledger-record-payment-student-select"
                    className="w-full rounded-[6px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    value={form.studentId}
                    onChange={(event) =>
                      onChange({ studentId: event.target.value })
                    }
                  >
                    <option value="" disabled>
                      Select a student...
                    </option>
                    <option value="all">
                      Apply to family balance (All students)
                    </option>
                    {family.students.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                      Payment amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                        $
                      </span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                        value={form.amount}
                        onChange={(event) =>
                          onChange({ amount: event.target.value })
                        }
                        data-testid="school-tuition-family-ledger-record-payment-amount-input"
                        className="w-full rounded-[6px] border border-border py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                      Date received
                    </label>
                    <TailwindDateField
                      value={form.receivedDate}
                      onChange={(event) =>
                        onChange({ receivedDate: event.target.value })
                      }
                      data-testid="school-tuition-family-ledger-record-payment-date-input"
                      className="w-full rounded-[6px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                      Payment method
                    </label>
                    <select
                      data-testid="school-tuition-family-ledger-record-payment-method-select"
                      className="w-full rounded-[6px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      value={form.method}
                      onChange={(event) =>
                        onChange({
                          method: event.target
                            .value as RecordPaymentFormState["method"],
                        })
                      }
                    >
                      <option value="Check">Check</option>
                      <option value="Cash">Cash</option>
                      <option value="ACH">ACH</option>
                      <option value="Wire">Wire</option>
                      <option value="Manual card capture">
                        Manual card capture
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                      Reference number
                    </label>
                    <input
                      type="text"
                      placeholder="Optional reference"
                      value={form.referenceCode}
                      onChange={(event) =>
                        onChange({ referenceCode: event.target.value })
                      }
                      data-testid="school-tuition-family-ledger-record-payment-reference-input"
                      className="w-full rounded-[6px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                    Notes (optional)
                  </label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(event) =>
                      onChange({ notes: event.target.value })
                    }
                    placeholder="Add any internal notes about this payment..."
                    data-testid="school-tuition-family-ledger-record-payment-notes-input"
                    className="w-full resize-none rounded-[6px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center border-t border-border bg-background-light p-6">
              <button
                type="button"
                disabled={!isValid || busy}
                onClick={onProcessPayment}
                data-testid="school-tuition-family-ledger-record-payment-process-button"
                className="mr-auto font-body text-sm font-medium text-primary transition-colors hover:text-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                Process Payment
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!isValid || busy}
                  onClick={onSubmit}
                  data-testid="school-tuition-family-ledger-record-payment-submit"
                  className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? "Recording…" : "Record payment"}
                </button>
              </div>
            </div>
          </div>

          <div
            className={`absolute inset-0 flex flex-col bg-surface-light transition-all duration-500 ease-out ${
              view === "process"
                ? "translate-y-0 opacity-100"
                : "translate-y-6 opacity-0 pointer-events-none"
            }`}
          >
            <div className="flex-1 space-y-5 overflow-y-auto p-6">
              <button
                type="button"
                onClick={onBackToEntry}
                data-testid="school-tuition-family-ledger-record-payment-process-back"
                className="inline-flex items-center gap-2 font-body text-xs font-medium uppercase tracking-[0.08em] text-text-secondary transition-colors hover:text-primary"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </button>

              <div className="space-y-2">
                <h3 className="font-title text-xl font-semibold text-iron-grey">
                  Process Payment
                </h3>
                <p className="font-body text-sm text-text-secondary">
                  Saved payment draft for {processedStudentLabel} in{" "}
                  {processDraft?.period ?? "the selected period"}.
                </p>
              </div>

              <div className="rounded-[12px] border border-dashed border-[rgba(38,151,162,0.35)] bg-muted/40 p-5">
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 text-xs text-text-secondary">
                    <span className="rounded-full bg-background-light px-3 py-1 font-body font-medium text-iron-grey">
                      Amount{" "}
                      {processDraft?.amount
                        ? formatCurrencyDollars(Number(processDraft.amount))
                        : "$0.00"}
                    </span>
                    <span className="rounded-full bg-background-light px-3 py-1 font-body font-medium text-iron-grey">
                      Privacy-safe manual settlement
                    </span>
                  </div>
                  <div
                    data-testid="school-tuition-family-ledger-record-payment-process-placeholder"
                    className="flex min-h-[320px] items-center justify-center rounded-[10px] border border-border bg-surface-light px-6 py-8 text-center"
                  >
                    <div className="space-y-2">
                      <p className="font-title text-lg font-semibold text-iron-grey">
                        Manual card settlement metadata only
                      </p>
                      <p className="font-body text-sm text-text-secondary">
                        Record the processor reference, operator context,
                        received date, and amount. Do not enter or store PAN,
                        CVV, or magstripe data in Intellio.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManagePayorsModal({
  isOpen,
  onClose,
  familyName,
  payers,
  draft,
  onDraftChange,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  familyName: string;
  payers: TuitionPayerSnapshot[];
  draft: PayorFormState;
  onDraftChange: (patch: Partial<PayorFormState>) => void;
  onSave: () => void;
}) {
  if (!isOpen) {
    return null;
  }

  const primaryPayer =
    payers.find((payer) => payer.isPrimary) ?? payers[0] ?? null;

  return (
    <div
      data-testid="school-tuition-family-ledger-payors-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        data-passive-click-surface="true"
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-[500px] overflow-hidden rounded-[12px] bg-surface-light shadow-xl">
        <div className="flex items-center justify-between border-b border-border p-5">
          <h2 className="font-heading text-lg font-semibold text-iron-grey">
            Manage Payors
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="school-tuition-family-ledger-payors-close"
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          <div className="mb-8">
            <h3 className="mb-3 font-title text-sm font-bold uppercase tracking-wider text-iron-grey">
              Default Payor
            </h3>
            <div className="flex items-center justify-between rounded-[8px] border border-border bg-muted p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary">
                  {(primaryPayer?.displayName ?? familyName).charAt(0)}
                </div>
                <div>
                  <p className="font-body text-sm font-semibold text-iron-grey">
                    {primaryPayer?.displayName ?? `${familyName} Family`}
                  </p>
                  <p className="font-body text-xs text-text-secondary">
                    {primaryPayer
                      ? `${primaryPayer.relationLabel}${primaryPayer.isPrimary ? " (Primary Account Holder)" : ""}`
                      : "Primary account holder"}
                  </p>
                </div>
              </div>
              <span className="rounded-full border border-aquamarine/30 bg-success-background px-2.5 py-1 text-xs font-medium text-jungle">
                Active
              </span>
            </div>
          </div>

          {payers.length > 1 ? (
            <div className="mb-8">
              <h3 className="mb-3 font-title text-sm font-bold uppercase tracking-wider text-iron-grey">
                Current payors
              </h3>
              <div
                data-testid="school-tuition-family-ledger-payors-list"
                className="space-y-3"
              >
                {payers.map((payer, index) => (
                  <div
                    key={payer.id}
                    data-testid={`school-tuition-family-ledger-payors-row-${index + 1}`}
                    className="flex items-center justify-between rounded-[8px] border border-border bg-white px-4 py-3"
                  >
                    <div>
                      <p className="font-body text-sm font-semibold text-iron-grey">
                        {payer.displayName}
                      </p>
                      <p className="font-body text-xs text-text-secondary">
                        {payer.relationLabel}
                        {payer.email ? ` • ${payer.email}` : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-body text-sm font-semibold text-iron-grey">
                        {payer.allocationPercent}%
                      </p>
                      <p className="font-body text-xs text-text-secondary">
                        {formatCurrency(payer.allocationBalanceCents)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <h3 className="mb-4 font-title text-sm font-bold uppercase tracking-wider text-iron-grey">
              Add Third-Party Payor
            </h3>
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                onSave();
              }}
            >
              <div>
                <label className="mb-1.5 block font-body text-xs font-medium text-iron-grey">
                  Relationship Label
                </label>
                <select
                  value={draft.relationLabel}
                  onChange={(event) =>
                    onDraftChange({ relationLabel: event.target.value })
                  }
                  data-testid="school-tuition-family-ledger-payors-relation-select"
                  className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="Parent">Parent</option>
                  <option value="Grandparent">Grandparent</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Sponsor">Sponsor</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block font-body text-xs font-medium text-iron-grey">
                    First Name
                  </label>
                  <input
                    type="text"
                    required
                    value={draft.firstName}
                    onChange={(event) =>
                      onDraftChange({ firstName: event.target.value })
                    }
                    data-testid="school-tuition-family-ledger-payors-first-name-input"
                    className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block font-body text-xs font-medium text-iron-grey">
                    Last Name
                  </label>
                  <input
                    type="text"
                    required
                    value={draft.lastName}
                    onChange={(event) =>
                      onDraftChange({ lastName: event.target.value })
                    }
                    data-testid="school-tuition-family-ledger-payors-last-name-input"
                    className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block font-body text-xs font-medium text-iron-grey">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={draft.email}
                    onChange={(event) =>
                      onDraftChange({ email: event.target.value })
                    }
                    data-testid="school-tuition-family-ledger-payors-email-input"
                    className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block font-body text-xs font-medium text-iron-grey">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={draft.phone}
                    onChange={(event) =>
                      onDraftChange({ phone: event.target.value })
                    }
                    data-testid="school-tuition-family-ledger-payors-phone-input"
                    className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block font-body text-xs font-medium text-iron-grey">
                  Expected Contribution
                </label>
                <div className="flex gap-2">
                  <select
                    value={draft.contributionType}
                    onChange={(event) =>
                      onDraftChange({
                        contributionType: event.target
                          .value as PayorFormState["contributionType"],
                      })
                    }
                    data-testid="school-tuition-family-ledger-payors-contribution-type-select"
                    className="w-24 rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="$">$</option>
                    <option value="%">%</option>
                  </select>
                  <input
                    type="text"
                    value={draft.contributionValue}
                    onChange={(event) =>
                      onDraftChange({ contributionValue: event.target.value })
                    }
                    placeholder={draft.contributionType === "%" ? "35" : "5000"}
                    data-testid="school-tuition-family-ledger-payors-contribution-value-input"
                    className="flex-1 rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-border pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="school-tuition-family-ledger-payors-save-button"
                  className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white transition-colors hover:bg-primary-hover"
                >
                  Save payor
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReversePaymentModal({
  isOpen,
  onClose,
  family,
  paymentEntry,
  form,
  onChange,
  onSubmit,
  busy,
}: {
  isOpen: boolean;
  onClose: () => void;
  family: TuitionFamilySnapshot;
  paymentEntry: SchoolLedgerEntry | null;
  form: ReversePaymentFormState;
  onChange: (patch: Partial<ReversePaymentFormState>) => void;
  onSubmit: () => void;
  busy: boolean;
}) {
  if (!isOpen || !paymentEntry) {
    return null;
  }

  const trimmedReason = form.reason.trim();
  const canSubmit =
    trimmedReason.length >= 10 && trimmedReason.length <= 500 && !busy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-iron-grey/25 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-lg flex-col overflow-hidden rounded-[16px] border border-border bg-surface-light shadow-lg">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-6">
          <h2 className="font-title text-xl font-semibold text-iron-grey">
            Reverse payment
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          <div className="rounded-[12px] bg-muted p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="font-body text-xs uppercase tracking-[0.06em] text-text-secondary">
                  Amount
                </p>
                <p className="mt-1 font-body text-sm font-semibold text-iron-grey">
                  {formatCurrencyDollars(
                    Math.abs(paymentEntry.amountCents) / 100,
                  )}
                </p>
              </div>
              <div>
                <p className="font-body text-xs uppercase tracking-[0.06em] text-text-secondary">
                  Date
                </p>
                <p className="mt-1 font-body text-sm font-semibold text-iron-grey">
                  {formatDate(paymentEntry.date)}
                </p>
              </div>
              <div>
                <p className="font-body text-xs uppercase tracking-[0.06em] text-text-secondary">
                  Method
                </p>
                <p className="mt-1 font-body text-sm font-semibold text-iron-grey">
                  {paymentEntry.sourceLabel}
                </p>
              </div>
              <div>
                <p className="font-body text-xs uppercase tracking-[0.06em] text-text-secondary">
                  Payer
                </p>
                <p className="mt-1 font-body text-sm font-semibold text-iron-grey">
                  {resolvePrimaryPayerName(family)}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Reason for reversal
            </label>
            <textarea
              rows={5}
              value={form.reason}
              onChange={(event) =>
                onChange({
                  reason: event.target.value.slice(0, 500),
                  error: null,
                })
              }
              placeholder="Describe why this payment is being reversed..."
              className="w-full resize-none rounded-[8px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="mt-1 text-right font-body text-xs text-text-secondary">
              {trimmedReason.length}/500
            </p>
          </div>

          <div className="rounded-[12px] border border-[rgba(255,112,95,0.22)] bg-[rgba(255,112,95,0.12)] px-4 py-3">
            <p className="font-body text-sm text-iron-grey">
              This will increase the family&apos;s balance by{" "}
              {formatCurrencyDollars(Math.abs(paymentEntry.amountCents) / 100)}{" "}
              and revert the affected installment. This action cannot be undone.
              No notification will be sent to the family.
            </p>
          </div>

          {form.error ? (
            <SupportErrorMessage>
              {form.error}
            </SupportErrorMessage>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="rounded-[6px] bg-strawberry px-4 py-2 font-body text-sm font-medium text-white transition-colors hover:bg-[#d52431] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Reversing…" : "Reverse payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function IssueRefundModal({
  isOpen,
  onClose,
  family,
  form,
  onChange,
  onSubmit,
  busy,
  maxRefundableCents,
  stripeEligiblePayments,
}: {
  isOpen: boolean;
  onClose: () => void;
  family: TuitionFamilySnapshot;
  form: IssueRefundFormState;
  onChange: (patch: Partial<IssueRefundFormState>) => void;
  onSubmit: () => void;
  busy: boolean;
  maxRefundableCents: number;
  stripeEligiblePayments: TuitionFamilySnapshot["transactions"];
}) {
  if (!isOpen) {
    return null;
  }

  const amountValue = Number(form.amount);
  const amountCents = Math.round(
    (Number.isFinite(amountValue) ? amountValue : 0) * 100,
  );
  const needsNotes = form.reason === "OTHER";
  const needsStripeSelection = form.method === "STRIPE_ORIGINAL";
  const canSubmit =
    amountCents >= 1 &&
    amountCents <= maxRefundableCents &&
    (!needsNotes || form.reasonNotes.trim().length > 0) &&
    (!needsStripeSelection || form.originalPaymentEntryId.trim().length > 0) &&
    !busy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-iron-grey/25 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-[16px] border border-border bg-surface-light shadow-lg">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-6">
          <h2 className="font-title text-xl font-semibold text-iron-grey">
            Issue refund
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6 overflow-y-auto p-6">
          <div className="rounded-[12px] bg-muted p-4">
            <p className="font-body text-xs uppercase tracking-[0.06em] text-text-secondary">
              Family account
            </p>
            <p className="mt-1 font-body text-base font-semibold text-iron-grey">
              {family.familyName}
            </p>
            <p className="mt-2 font-body text-sm text-text-secondary">
              Maximum refundable this year:{" "}
              {formatCurrencyDollars(maxRefundableCents / 100)}
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                  $
                </span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.amount}
                  onChange={(event) =>
                    onChange({ amount: event.target.value, error: null })
                  }
                  className="w-full rounded-[8px] border border-border py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <p className="mt-1 font-body text-xs text-text-secondary">
                Max {formatCurrencyDollars(maxRefundableCents / 100)}
              </p>
            </div>

            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Reason
              </label>
              <select
                value={form.reason}
                onChange={(event) =>
                  onChange({
                    reason: event.target
                      .value as IssueRefundFormState["reason"],
                    error: null,
                  })
                }
                className="w-full rounded-[8px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="WITHDRAWAL">Withdrawal</option>
                <option value="OVERPAYMENT">Overpayment</option>
                <option value="BILLING_ERROR">Billing error</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>

          {needsNotes ? (
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Reason notes
              </label>
              <textarea
                rows={4}
                value={form.reasonNotes}
                onChange={(event) =>
                  onChange({
                    reasonNotes: event.target.value.slice(0, 500),
                    error: null,
                  })
                }
                className="w-full resize-none rounded-[8px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <p className="mt-1 text-right font-body text-xs text-text-secondary">
                {form.reasonNotes.trim().length}/500
              </p>
            </div>
          ) : null}

          <div>
            <label className="mb-2 block font-body text-sm font-medium text-iron-grey">
              Method
            </label>
            <div className="space-y-2">
              {(
                [
                  [
                    "STRIPE_ORIGINAL",
                    "Refund to original payment method (Stripe)",
                  ],
                  ["CHECK", "Check"],
                  ["OTHER", "Other"],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className="flex cursor-pointer items-center gap-3 rounded-[10px] border border-border px-3 py-3"
                >
                  <input
                    type="radio"
                    name="refund-method"
                    checked={form.method === value}
                    onChange={() => onChange({ method: value, error: null })}
                    className="h-4 w-4 border-border text-primary focus:ring-primary"
                  />
                  <span className="font-body text-sm text-iron-grey">
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {needsStripeSelection ? (
            <div>
              <label className="mb-2 block font-body text-sm font-medium text-iron-grey">
                Recent Stripe payments
              </label>
              <div className="space-y-2">
                {stripeEligiblePayments.length > 0 ? (
                  stripeEligiblePayments.map((payment) => (
                    <label
                      key={payment.id}
                      className={`flex cursor-pointer items-center justify-between gap-4 rounded-[10px] border px-4 py-3 ${
                        form.originalPaymentEntryId === payment.id
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="stripe-payment-entry"
                          checked={form.originalPaymentEntryId === payment.id}
                          onChange={() =>
                            onChange({
                              originalPaymentEntryId: payment.id,
                              error: null,
                            })
                          }
                          className="h-4 w-4 border-border text-primary focus:ring-primary"
                        />
                        <div>
                          <p className="font-body text-sm font-medium text-iron-grey">
                            {formatDate(
                              payment.observedAt ??
                                payment.internalEffectiveDate ??
                                payment.createdAt,
                            )}
                          </p>
                          <p className="font-body text-xs text-text-secondary">
                            {formatCurrencyDollars(
                              Math.abs(payment.amountCents) / 100,
                            )}{" "}
                            • {family.paymentMethodSummary}
                          </p>
                        </div>
                      </div>
                    </label>
                  ))
                ) : (
                  <div className="rounded-[10px] border border-border bg-muted/40 px-4 py-3 font-body text-sm text-text-secondary">
                    No Stripe payments from the last 180 days are available for
                    automatic refunding.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Reference number (optional)
              </label>
              <input
                type="text"
                value={form.referenceNumber}
                onChange={(event) =>
                  onChange({ referenceNumber: event.target.value, error: null })
                }
                placeholder="Check number or external reference"
                className="w-full rounded-[8px] border border-border px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {form.error ? (
            <SupportErrorMessage>
              {form.error}
            </SupportErrorMessage>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Issuing…" : "Issue refund"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddChargeModal({
  isOpen,
  onClose,
  family,
  form,
  onChange,
  onSubmit,
  busy,
}: {
  isOpen: boolean;
  onClose: () => void;
  family: TuitionFamilySnapshot | null;
  form: AddChargeFormState;
  onChange: (patch: Partial<AddChargeFormState>) => void;
  onSubmit: () => void;
  busy: boolean;
}) {
  if (!isOpen || !family) {
    return null;
  }

  const amountValue = Number(form.amount);
  const canSubmit = Boolean(
    form.chargeName.trim() &&
      Number.isFinite(amountValue) &&
      amountValue >= 0.01 &&
      form.dueDate &&
      (!form.perStudent || form.studentIds.length > 0) &&
      !busy,
  );
  const fieldClassName =
    "w-full rounded-[10px] border border-[#d8dee6] bg-white px-4 py-3 font-body text-[15px] text-iron-grey shadow-[inset_0_1px_2px_rgba(16,24,40,0.03)] outline-none transition-colors placeholder:text-[#98a2b3] focus:border-[#2697a2] focus:ring-2 focus:ring-[#2697a2]/15";
  const labelClassName =
    "mb-2 block font-body text-sm font-medium text-iron-grey";
  const requiredAsterisk = <span className="text-strawberry">*</span>;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#101828]/20 p-4 backdrop-blur-[2px]"
      style={{ marginBlockStart: 0 }}
    >
      <div className="flex max-h-[92vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[18px] border border-[#e7edf3] bg-white shadow-[0_24px_60px_rgba(16,24,40,0.18)]">
        <div className="flex items-center justify-between border-b border-[#e7edf3] px-5 py-4">
          <h2 className="font-title text-[18px] font-semibold text-iron-grey">
            Add charge
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-[#98a2b3] transition-colors hover:bg-muted hover:text-iron-grey"
            aria-label="Close add charge modal"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto px-5 py-5">
          <div>
            <label className={labelClassName}>Family</label>
            <div className="rounded-[10px] border border-[#d8dee6] bg-[#f8fafc] px-4 py-3 font-body text-[15px] font-medium text-iron-grey shadow-[inset_0_1px_2px_rgba(16,24,40,0.02)]">
              {family.familyName}
            </div>
          </div>

          <div>
            <label className={labelClassName}>
              Charge name {requiredAsterisk}
            </label>
            <input
              type="text"
              value={form.chargeName}
              onChange={(event) =>
                onChange({ chargeName: event.target.value, error: null })
              }
              placeholder="e.g., 6th Grade DC Trip"
              className={fieldClassName}
            />
          </div>

          <div>
            <label className={labelClassName}>Amount {requiredAsterisk}</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-body text-[24px] leading-none text-[#98a2b3]">
                $
              </span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={(event) =>
                  onChange({ amount: event.target.value, error: null })
                }
                placeholder="0.00"
                className={`${fieldClassName} pl-10`}
              />
            </div>
          </div>

          <div>
            <label className={labelClassName}>Charge type</label>
            <div className="inline-flex rounded-[10px] bg-[#f5f8fa] p-1">
              <button
                type="button"
                onClick={() =>
                  onChange({
                    perStudent: false,
                    studentIds: [],
                    error: null,
                  })
                }
                className={`rounded-[8px] px-5 py-2.5 font-body text-[15px] font-medium transition-colors ${
                  !form.perStudent
                    ? "bg-white text-[#2697a2] shadow-sm"
                    : "text-[#667085] hover:text-iron-grey"
                }`}
              >
                Per-family
              </button>
              <button
                type="button"
                onClick={() =>
                  onChange({
                    perStudent: true,
                    studentIds: form.studentIds,
                    error: null,
                  })
                }
                className={`rounded-[8px] px-5 py-2.5 font-body text-[15px] font-medium transition-colors ${
                  form.perStudent
                    ? "bg-white text-[#2697a2] shadow-sm"
                    : "text-[#667085] hover:text-iron-grey"
                }`}
              >
                Per-student
              </button>
            </div>
          </div>

          {form.perStudent ? (
            <div>
              <label className={labelClassName}>
                Students {requiredAsterisk}
              </label>
              <div className="space-y-2 rounded-[12px] border border-[#d8dee6] bg-[#f8fafc] p-3">
                {family.students.map((student) => (
                  <label
                    key={student.id}
                    className="flex cursor-pointer items-center justify-between gap-4 rounded-[10px] border border-[#e7edf3] bg-white px-3 py-3"
                  >
                    <div>
                      <p className="font-body text-sm font-medium text-iron-grey">
                        {student.name}
                      </p>
                      <p className="mt-1 font-body text-xs text-text-secondary">
                        {student.gradeLabel}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={form.studentIds.includes(student.id)}
                      onChange={() =>
                        onChange({
                          studentIds: toggleSelection(
                            form.studentIds,
                            student.id,
                          ),
                          error: null,
                        })
                      }
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <label className={labelClassName}>
              Due date {requiredAsterisk}
            </label>
            <div>
              <TailwindDateField
                min={new Date().toISOString().slice(0, 10)}
                value={form.dueDate}
                onChange={(event) =>
                  onChange({ dueDate: event.target.value, error: null })
                }
                className={fieldClassName}
              />
            </div>
          </div>

          <div>
            <label className={labelClassName}>Billing category</label>
            <div className="relative">
              <select
                value={form.billingCategory}
                onChange={(event) =>
                  onChange({
                    billingCategory: event.target.value as BillingCategoryValue,
                    error: null,
                  })
                }
                className={`${fieldClassName} appearance-none pr-10`}
              >
                {(
                  [
                    "tuition",
                    "fees",
                    "activities",
                    "transportation",
                    "food_service",
                    "other",
                  ] as BillingCategoryValue[]
                ).map((option) => (
                  <option key={option} value={option}>
                    {formatBillingCategoryLabel(option)}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
            </div>
          </div>

          <div>
            <label className={labelClassName}>
              Notes{" "}
              <span className="font-normal text-text-tertiary">(optional)</span>
            </label>
            <textarea
              rows={4}
              value={form.notes}
              placeholder="Add any additional details..."
              onChange={(event) =>
                onChange({
                  notes: event.target.value.slice(0, 500),
                  error: null,
                })
              }
              className={`${fieldClassName} min-h-[104px] resize-none`}
            />
            <div className="mt-2 text-right font-body text-sm text-text-tertiary">
              {form.notes.length}/500
            </div>
          </div>

          {form.error ? (
            <SupportErrorMessage>
              {form.error}
            </SupportErrorMessage>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-[#e7edf3] bg-white px-5 py-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[8px] border border-[#d8dee6] bg-white px-5 py-2.5 font-body text-[15px] font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="rounded-[8px] bg-[#9fe3e6] px-5 py-2.5 font-body text-[15px] font-semibold text-white transition-colors hover:bg-[#89d8dc] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Adding…" : "Add charge"}
          </button>
        </div>
      </div>
    </div>
  );
}

type BulkChargePreviewSnapshot = {
  studentCount: number;
  familyCount: number;
  students: Array<{
    id: string;
    name: string;
    grade: string | null;
    familyName: string;
  }>;
};

type BulkChargeJobSnapshot = {
  id: string;
  status: string;
  studentCount: number;
  familyCount: number;
  processedInvoiceCount?: number | null;
};

type BulkChargePreviewMode = "live" | "fallback" | "empty";

type ButtonGroupOptionProps = {
  options: string[];
  selectedValues: string[];
  onToggle: (option: string) => void;
  className?: string;
};

function ButtonGroup({
  options,
  selectedValues,
  onToggle,
  className = "",
}: ButtonGroupOptionProps) {
  return (
    <div
      className={`inline-flex w-full overflow-hidden rounded-[10px] border border-[#d8dee6] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ${className}`}
    >
      {options.map((option, index) => {
        const isSelected = selectedValues.includes(option);
        return (
          <button
            key={option}
            type="button"
            onClick={() => onToggle(option)}
            className={`flex-1 border-r border-[#d8dee6] px-2 py-2 text-[13px] font-body font-medium leading-none transition-colors last:border-r-0 ${
              isSelected
                ? "bg-primary/10 text-primary"
                : "bg-white text-iron-grey hover:bg-muted"
            }`}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

function BulkChargeModal({
  isOpen,
  onClose,
  form,
  onChange,
  onSubmit,
  busy,
  preview,
  previewLoading,
  previewMode,
  previewAlertMessage,
  previewErrorMessage,
  gradeOptions,
  divisionOptions,
  rateCategoryOptions,
  activeJob,
}: {
  isOpen: boolean;
  onClose: () => void;
  form: BulkChargeFormState;
  onChange: (patch: Partial<BulkChargeFormState>) => void;
  onSubmit: () => void;
  busy: boolean;
  preview: BulkChargePreviewSnapshot | null;
  previewLoading: boolean;
  previewMode: BulkChargePreviewMode;
  previewAlertMessage: string | null;
  previewErrorMessage: string | null;
  gradeOptions: string[];
  divisionOptions: string[];
  rateCategoryOptions: string[];
  activeJob: BulkChargeJobSnapshot | null;
}) {
  if (!isOpen) {
    return null;
  }

  const amountValue = Number(form.amountPerStudent);
  const studentCount = preview?.studentCount ?? 0;
  const familyCount = preview?.familyCount ?? 0;
  const totalAmount = Number.isFinite(amountValue)
    ? amountValue * studentCount
    : 0;
  const canPrepare = Boolean(
    form.chargeName.trim() &&
      Number.isFinite(amountValue) &&
      amountValue >= 0.01 &&
      form.dueDate &&
      studentCount > 0 &&
      !busy,
  );
  const fieldClassName =
    "w-full rounded-[10px] border border-[#d8dee6] bg-white px-4 py-3 font-body text-[15px] text-iron-grey shadow-[inset_0_1px_2px_rgba(16,24,40,0.03)] outline-none transition-colors placeholder:text-[#98a2b3] focus:border-[#2697a2] focus:ring-2 focus:ring-[#2697a2]/15";
  const sectionTitleClassName =
    "font-title text-[13px] font-semibold uppercase tracking-[0.05em] text-iron-grey";
  const fieldLabelClassName =
    "mb-2 block font-body text-sm font-medium text-iron-grey";
  const requiredAsterisk = <span className="text-strawberry">*</span>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#101828]/20 p-4 backdrop-blur-[2px]">
      <div className="flex max-h-[94vh] w-full max-w-[720px] flex-col overflow-hidden rounded-[18px] border border-[#e7edf3] bg-white shadow-[0_24px_60px_rgba(16,24,40,0.18)]">
        <div className="flex items-center justify-between border-b border-[#e7edf3] px-5 py-4">
          <h2 className="font-title text-[18px] font-semibold text-iron-grey">
            Bulk charge
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-[#98a2b3] transition-colors hover:bg-muted hover:text-iron-grey"
            aria-label="Close bulk charge modal"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid flex-1 gap-0 overflow-y-auto lg:grid-cols-[minmax(0,1.15fr)_280px]">
          <div className="space-y-8 px-5 py-5">
            <section className="space-y-5">
              <h3 className={sectionTitleClassName}>Charge details</h3>

              <div className="space-y-4">
                <div>
                  <label className={fieldLabelClassName}>
                    Charge name {requiredAsterisk}
                  </label>
                  <input
                    type="text"
                    value={form.chargeName}
                    onChange={(event) =>
                      onChange({
                        chargeName: event.target.value,
                        confirm: false,
                        error: null,
                      })
                    }
                    placeholder="e.g., Annual Technology Fee"
                    className={fieldClassName}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={fieldLabelClassName}>
                      Amount per student {requiredAsterisk}
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-body text-[24px] leading-none text-[#98a2b3]">
                        $
                      </span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={form.amountPerStudent}
                        onChange={(event) =>
                          onChange({
                            amountPerStudent: event.target.value,
                            confirm: false,
                            error: null,
                          })
                        }
                        placeholder="0.00"
                        className={`${fieldClassName} pl-10`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={fieldLabelClassName}>
                      Due date {requiredAsterisk}
                    </label>
                    <div>
                      <TailwindDateField
                        min={new Date().toISOString().slice(0, 10)}
                        value={form.dueDate}
                        onChange={(event) =>
                          onChange({
                            dueDate: event.target.value,
                            confirm: false,
                            error: null,
                          })
                        }
                        className={fieldClassName}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className={fieldLabelClassName}>
                    Billing category
                  </label>
                  <div className="relative">
                    <select
                      value={form.billingCategory}
                      onChange={(event) =>
                        onChange({
                          billingCategory: event.target
                            .value as BillingCategoryValue,
                          confirm: false,
                          error: null,
                        })
                      }
                      className={`${fieldClassName} appearance-none pr-10`}
                    >
                      {(
                        [
                          "tuition",
                          "fees",
                          "activities",
                          "transportation",
                          "food_service",
                          "other",
                        ] as BillingCategoryValue[]
                      ).map((option) => (
                        <option key={option} value={option}>
                          {formatBillingCategoryLabel(option)}
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
                  </div>
                </div>

                <div>
                  <label className={fieldLabelClassName}>
                    Notes{" "}
                    <span className="font-normal text-text-tertiary">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    placeholder="Add any additional details..."
                    onChange={(event) =>
                      onChange({
                        notes: event.target.value.slice(0, 500),
                        confirm: false,
                        error: null,
                      })
                    }
                    className={`${fieldClassName} min-h-[84px] resize-none`}
                  />
                </div>
              </div>
            </section>

            <section className="space-y-5">
              <h3 className={sectionTitleClassName}>Target segment</h3>

              <div className="space-y-5">
                <div>
                  <label className={fieldLabelClassName}>Gender</label>
                  <div className="flex flex-wrap items-center gap-5">
                    {(["ALL", "BOYS", "GIRLS"] as const).map((option) => (
                      <label
                        key={option}
                        className="flex cursor-pointer items-center gap-2 font-body text-[15px] text-iron-grey"
                      >
                        <input
                          type="radio"
                          name="bulk-charge-gender"
                          checked={form.gender === option}
                          onChange={() =>
                            onChange({ gender: option, confirm: false })
                          }
                          className="h-4 w-4 border-border text-primary focus:ring-primary"
                        />
                        <span>
                          {option === "ALL"
                            ? "All"
                            : option === "BOYS"
                              ? "Boys"
                              : "Girls"}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={fieldLabelClassName}>
                    Grades{" "}
                    <span className="font-normal text-text-tertiary">
                      (leave empty for all)
                    </span>
                  </label>
                  <ButtonGroup
                    options={gradeOptions}
                    selectedValues={form.grades}
                    onToggle={(grade) =>
                      onChange({
                        grades: toggleSelection(form.grades, grade),
                        confirm: false,
                      })
                    }
                  />
                </div>

                <div>
                  <label className={fieldLabelClassName}>
                    Divisions{" "}
                    <span className="font-normal text-text-tertiary">
                      (leave empty for all)
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {divisionOptions.map((division) => {
                      const isSelected = form.divisions.includes(division);
                      return (
                        <button
                          type="button"
                          key={division}
                          onClick={() =>
                            onChange({
                              divisions: toggleSelection(
                                form.divisions,
                                division,
                              ),
                              confirm: false,
                            })
                          }
                          className={`rounded-full border px-3 py-1.5 text-[14px] font-medium transition-colors ${
                            isSelected
                              ? "border-primary bg-primary text-white shadow-sm"
                              : "border-[#d8dee6] bg-white text-[#667085] hover:bg-muted"
                          }`}
                        >
                          {division}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className={fieldLabelClassName}>
                    Rate Categories{" "}
                    <span className="font-normal text-text-tertiary">
                      (leave empty for all)
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {rateCategoryOptions.length > 0 ? (
                      rateCategoryOptions.map((rateCategory) => {
                        const isSelected =
                          form.rateCategories.includes(rateCategory);
                        return (
                          <button
                            type="button"
                            key={rateCategory}
                            onClick={() =>
                              onChange({
                                rateCategories: toggleSelection(
                                  form.rateCategories,
                                  rateCategory,
                                ),
                                confirm: false,
                              })
                            }
                            className={`rounded-full border px-3 py-1.5 text-[14px] font-medium transition-colors ${
                              isSelected
                                ? "border-primary bg-primary text-white shadow-sm"
                                : "border-[#d8dee6] bg-white text-[#667085] hover:bg-muted"
                            }`}
                          >
                            {rateCategory}
                          </button>
                        );
                      })
                    ) : (
                      <p className="font-body text-sm text-text-secondary">
                        No rate categories are configured in the current
                        dashboard data.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="border-l border-[#e7edf3] bg-white px-5 py-6">
            <div className="rounded-[12px] border border-[#d8dee6] bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.03)]">
              <h3 className={sectionTitleClassName}>Live preview</h3>
              <p className="mt-4 font-body text-[15px] leading-8 text-iron-grey">
                This will charge{" "}
                <span className="font-semibold">
                  {previewLoading ? "…" : studentCount}
                </span>{" "}
                students across{" "}
                <span className="font-semibold">
                  {previewLoading ? "…" : familyCount}
                </span>{" "}
                families.
              </p>

              {previewAlertMessage ? (
                <div className="mt-4 rounded-[12px] border border-[rgba(255,176,32,0.28)] bg-[rgba(255,176,32,0.12)] px-4 py-3">
                  <p className="font-body text-sm text-iron-grey">
                    {previewAlertMessage}
                  </p>
                  {previewErrorMessage ? (
                    <p className="mt-1 font-body text-xs text-text-secondary">
                      {previewErrorMessage}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <button
                type="button"
                onClick={() => onChange({ showStudents: !form.showStudents })}
                className="mt-5 flex w-full items-center justify-between rounded-[10px] border border-[#d8dee6] bg-white px-4 py-3 text-left font-body text-[15px] font-medium text-iron-grey transition-colors hover:bg-muted"
              >
                <span>
                  {form.showStudents
                    ? "Hide student list"
                    : "View student list"}
                </span>
                {form.showStudents ? (
                  <ChevronUpIcon className="h-4 w-4 text-text-secondary" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
                )}
              </button>

              {form.showStudents ? (
                <div className="mt-4 max-h-[280px] overflow-y-auto rounded-[10px] border border-[#d8dee6] bg-white">
                  {(preview?.students ?? []).length > 0 ? (
                    <div className="divide-y divide-[#e7edf3]">
                      {(preview?.students ?? []).map((student) => (
                        <div key={student.id} className="px-4 py-3">
                          <p className="font-body text-[15px] font-semibold leading-6 text-iron-grey">
                            {student.name}
                          </p>
                          <p className="mt-0.5 font-body text-[13px] leading-5 text-text-secondary">
                            {student.grade ?? "Unassigned"}{" "}
                            <span className="mx-1 text-[#98a2b3]">•</span>{" "}
                            {student.familyName}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-5">
                      <p className="font-body text-sm text-text-secondary">
                        {previewLoading
                          ? "Loading student preview..."
                          : previewMode === "fallback"
                            ? "No active students from the current dashboard match the selected filters."
                            : "No active students match the selected filters."}
                      </p>
                    </div>
                  )}
                </div>
              ) : null}

              {form.confirm ? (
                <div className="mt-4 rounded-[12px] border border-[rgba(255,112,95,0.22)] bg-[rgba(255,112,95,0.12)] px-4 py-3">
                  <p className="font-body text-sm text-iron-grey">
                    You are about to charge {studentCount} students across{" "}
                    {familyCount} families a total of{" "}
                    {formatCurrencyDollars(totalAmount)}. This cannot be undone.
                    Proceed?
                  </p>
                  {previewMode === "fallback" ? (
                    <p className="mt-2 font-body text-xs text-text-secondary">
                      Live preview is unavailable, so final charged students may
                      differ from the fallback list shown here.
                    </p>
                  ) : null}
                </div>
              ) : null}

              {activeJob && activeJob.status === "processing" ? (
                <div className="mt-4 rounded-[12px] border border-border bg-muted/50 px-4 py-3">
                  <p className="font-body text-sm font-medium text-iron-grey">
                    Processing bulk charge job…
                  </p>
                  <p className="mt-1 font-body text-xs text-text-secondary">
                    {activeJob.processedInvoiceCount ?? 0} of{" "}
                    {activeJob.studentCount} invoices recorded so far.
                  </p>
                </div>
              ) : null}

              {form.error ? (
                <SupportErrorMessage className="mt-4">
                  {form.error}
                </SupportErrorMessage>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-[#e7edf3] bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[8px] border border-[#d8dee6] bg-white px-5 py-2.5 font-body text-[15px] font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          {!form.confirm ? (
            <button
              type="button"
              onClick={() => onChange({ confirm: true })}
              disabled={!canPrepare}
              className="rounded-[8px] bg-[#9fe3e6] px-5 py-2.5 font-body text-[15px] font-semibold text-white transition-colors hover:bg-[#89d8dc] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Charge {studentCount} students
            </button>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canPrepare}
              className="rounded-[8px] bg-[#2697a2] px-5 py-2.5 font-body text-[15px] font-semibold text-white transition-colors hover:bg-[#1d7e87] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Charging…" : "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChargebackAlertBanner({
  entry,
}: {
  entry: TuitionFamilySnapshot["transactions"][number];
}) {
  const disputeId = entry.disputeId ?? entry.stripeCorrelation?.disputeId ?? "";
  const stripeUrl = `https://dashboard.stripe.com/disputes/${encodeURIComponent(disputeId)}`;
  const filedOn =
    entry.observedAt ?? entry.internalEffectiveDate ?? entry.createdAt;
  const reason = extractChargebackReason(entry.description);

  return (
    <div className="flex w-full items-start gap-3 rounded-[12px] border-l-4 border-[#fe2c3b] bg-[rgba(254,44,59,0.08)] px-5 py-4">
      <AlertCircleIcon className="mt-0.5 h-5 w-5 text-[#fe2c3b]" />
      <div className="flex-1">
        <p className="font-body text-sm font-semibold text-iron-grey">
          Active chargeback dispute
        </p>
        <p className="mt-1 font-body text-sm text-iron-grey">
          A dispute for{" "}
          {formatCurrencyDollars(Math.abs(entry.amountCents) / 100)} was filed
          on {formatDate(filedOn)}. Reason: {reason}. Response deadline:{" "}
          {formatDate(entry.disputeDeadline)}.
        </p>
      </div>
      <a
        href={stripeUrl}
        target="_blank"
        rel="noreferrer"
        className="font-body text-sm font-semibold text-strawberry transition-colors hover:text-[#d52431]"
      >
        View in Stripe Dashboard
      </a>
    </div>
  );
}

function SchoolTuitionFamilyLedgerPage({
  snapshotOverride = null,
  initialError = null,
  initialFamilyLedgerOverlay = null,
}: Omit<SchoolTuitionOwnedPageProps, "suppressSelectedFamilyDetails">) {
  const { familyId = "" } = useParams<{ familyId: string }>();
  const location = useLocation();
  const navigationState =
    location.state as SchoolTuitionFamilyNavigationState | null;
  const seededFamily = useMemo(
    () =>
      snapshotOverride?.families.find((entry) => entry.familyId === familyId) ??
      navigationState?.familySnapshot ??
      null,
    [familyId, navigationState?.familySnapshot, snapshotOverride?.families],
  );
  const seededLedgerSnapshot = useMemo(
    () =>
      seededFamily
        ? buildSeedFamilyLedgerSnapshot(ACADEMIC_YEAR, seededFamily)
        : null,
    [seededFamily],
  );
  const familyLedgerQuery = useSchoolTuitionFamilyLedgerObjectTree({
    academicYear: ACADEMIC_YEAR,
    familyId,
    seededSnapshot: seededLedgerSnapshot,
  });
  const resolvedInitialFamilyLedgerOverlay =
    navigationState?.initialOverlay ?? initialFamilyLedgerOverlay;
  const [createAdHocCharge, createAdHocChargeState] =
    useCreateAdHocChargeMutation();
  const [reverseTuitionPayment, reverseTuitionPaymentState] =
    useReverseTuitionPaymentMutation();
  const [issueTuitionRefund, issueTuitionRefundState] =
    useIssueTuitionRefundMutation();
  const ledgerSnapshot = familyLedgerQuery.snapshot;
  const loading = familyLedgerQuery.isLoading;
  const error = initialError ?? familyLedgerQuery.error;
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { toasts, dismissToast, pushToast } = useTransientTuitionToasts();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(
    resolvedInitialFamilyLedgerOverlay === "record-payment",
  );
  const [isPayorsModalOpen, setIsPayorsModalOpen] = useState(
    resolvedInitialFamilyLedgerOverlay === "payors",
  );
  const [isRecurringFeeModalOpen, setIsRecurringFeeModalOpen] = useState(
    resolvedInitialFamilyLedgerOverlay === "recurring-fees",
  );
  const [isReverseModalOpen, setIsReverseModalOpen] = useState(false);
  const [selectedReverseEntry, setSelectedReverseEntry] =
    useState<SchoolLedgerEntry | null>(null);
  const [reversePaymentForm, setReversePaymentForm] =
    useState<ReversePaymentFormState>(buildReversePaymentDefaults());
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [issueRefundForm, setIssueRefundForm] = useState<IssueRefundFormState>(
    buildIssueRefundDefaults(),
  );
  const [isAddChargeModalOpen, setIsAddChargeModalOpen] = useState(false);
  const [addChargeForm, setAddChargeForm] = useState<AddChargeFormState>(
    buildAddChargeDefaults(),
  );
  const [typeFilter, setTypeFilter] = useState<Set<SchoolLedgerType>>(
    new Set(),
  );
  const [dateFilter, setDateFilter] = useState<SchoolLedgerDateRange>("all");
  const [childFilter, setChildFilter] = useState("All children");
  const [activeFilterMenu, setActiveFilterMenu] = useState<
    "type" | "date" | "child" | null
  >(null);
  const [activeFilterMenuPosition, setActiveFilterMenuPosition] =
    useState<FixedLayerPosition | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [activeMenuPosition, setActiveMenuPosition] =
    useState<FixedLayerPosition | null>(null);
  const [showUtilityActions, setShowUtilityActions] = useState(false);
  const [utilityMenuPosition, setUtilityMenuPosition] =
    useState<FixedLayerPosition | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<SchoolLedgerEditDraft>({});
  const [ledgerEntries, setLedgerEntries] = useState<SchoolLedgerEntry[]>([]);
  const [recordPaymentModalView, setRecordPaymentModalView] =
    useState<RecordPaymentModalView>("entry");
  const [recordPaymentProcessDraft, setRecordPaymentProcessDraft] =
    useState<RecordPaymentFormState | null>(null);
  const [recordPaymentForm, setRecordPaymentForm] =
    useState<RecordPaymentFormState>({
      method: "Check",
      amount: "",
      studentId: "",
      period: "",
      receivedDate: new Date().toISOString().slice(0, 10),
      referenceCode: "",
      notes: "",
    });
  const [localPayers, setLocalPayers] = useState<TuitionPayerSnapshot[]>([]);
  const [payorDraft, setPayorDraft] = useState<PayorFormState>(
    buildPayorFormDefaults(),
  );
  const menuRef = useRef<HTMLDivElement | null>(null);
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const filterMenuPortalRef = useRef<HTMLDivElement | null>(null);
  const utilityMenuRef = useRef<HTMLDivElement | null>(null);
  const utilityMenuPortalRef = useRef<HTMLDivElement | null>(null);

  const family = ledgerSnapshot?.family ?? null;
  const eligibleStripePayments = useMemo(
    () =>
      family?.transactions.filter((transaction) =>
        isStripePaymentEligibleForRefund(transaction),
      ) ?? [],
    [family?.transactions],
  );
  const maxRefundableCents = useMemo(
    () =>
      Math.max(
        0,
        (family?.transactions ?? []).reduce((sum, transaction) => {
          if (transaction.status === "voided") {
            return sum;
          }
          if (
            transaction.entryType === "payment" ||
            transaction.entryType === "refund" ||
            transaction.entryType === "reversal" ||
            transaction.entryType === "chargeback"
          ) {
            return sum - transaction.amountCents;
          }
          return sum;
        }, 0),
      ),
    [family?.transactions],
  );
  const activeChargebackEntry = useMemo(
    () =>
      family?.transactions.find((transaction) => {
        if (
          transaction.entryType !== "chargeback" ||
          !transaction.disputeDeadline
        ) {
          return false;
        }
        const deadline = Date.parse(transaction.disputeDeadline);
        return Number.isFinite(deadline) && deadline > Date.now();
      }) ?? null,
    [family?.transactions],
  );
  const closeRowMenu = useCallback(() => {
    setActiveMenuId(null);
    setActiveMenuPosition(null);
  }, []);
  const closeFilterMenu = useCallback(() => {
    setActiveFilterMenu(null);
    setActiveFilterMenuPosition(null);
  }, []);
  const closeUtilityMenu = useCallback(() => {
    setShowUtilityActions(false);
    setUtilityMenuPosition(null);
  }, []);

  useEffect(() => {
    if (!error) {
      return;
    }
    pushToast({
      message: error,
      variant: "error",
      testId: "school-tuition-family-ledger-error-toast",
    });
  }, [error, pushToast]);

  useEffect(() => {
    if (!actionError) {
      return;
    }
    pushToast({
      message: actionError,
      variant: "error",
      testId: "school-tuition-family-ledger-action-error-toast",
    });
  }, [actionError, pushToast]);

  useEffect(() => {
    if (!actionFeedback) {
      return;
    }
    pushToast({
      message: actionFeedback,
      variant: "success",
      testId: "school-tuition-family-ledger-action-feedback-toast",
    });
  }, [actionFeedback, pushToast]);

  useEffect(() => {
    if (loading || family) {
      return;
    }
    pushToast({
      message:
        "The requested tuition family could not be found on the active dashboard.",
      variant: "error",
      testId: "school-tuition-family-ledger-not-found-toast",
    });
  }, [family, loading, pushToast]);

  useEffect(() => {
    if (!family) {
      setLedgerEntries([]);
      setLocalPayers([]);
      return;
    }
    setLedgerEntries(buildFamilyLedgerEntries(family));
    setLocalPayers(ledgerSnapshot?.payers ?? family.payers);
    setRecordPaymentForm(buildRecordPaymentDefaults(family));
    setRecordPaymentModalView("entry");
    setRecordPaymentProcessDraft(null);
    setReversePaymentForm(buildReversePaymentDefaults());
    setIssueRefundForm(buildIssueRefundDefaults());
    setAddChargeForm(buildAddChargeDefaults());
    setPayorDraft(buildPayorFormDefaults());
    setIsPaymentModalOpen(
      resolvedInitialFamilyLedgerOverlay === "record-payment",
    );
    setIsPayorsModalOpen(resolvedInitialFamilyLedgerOverlay === "payors");
    setIsRecurringFeeModalOpen(
      resolvedInitialFamilyLedgerOverlay === "recurring-fees",
    );
    setIsReverseModalOpen(false);
    setSelectedReverseEntry(null);
    setIsRefundModalOpen(false);
    setIsAddChargeModalOpen(false);
    setEditingId(null);
    setActiveMenuId(null);
    setActiveMenuPosition(null);
    setShowUtilityActions(false);
    setUtilityMenuPosition(null);
    setTypeFilter(new Set());
    setDateFilter("all");
    setChildFilter("All children");
    setActiveFilterMenu(null);
    setActiveFilterMenuPosition(null);
  }, [
    family?.familyId,
    ledgerSnapshot?.payers,
    resolvedInitialFamilyLedgerOverlay,
  ]);

  const closeRecordPaymentModal = () => {
    setIsPaymentModalOpen(false);
    setRecordPaymentModalView("entry");
    setRecordPaymentProcessDraft(null);
    if (family) {
      setRecordPaymentForm(buildRecordPaymentDefaults(family));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeRowMenu();
      }
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(event.target as Node) &&
        filterMenuPortalRef.current &&
        !filterMenuPortalRef.current.contains(event.target as Node)
      ) {
        closeFilterMenu();
      }
      if (
        utilityMenuRef.current &&
        !utilityMenuRef.current.contains(event.target as Node) &&
        utilityMenuPortalRef.current &&
        !utilityMenuPortalRef.current.contains(event.target as Node)
      ) {
        closeUtilityMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeFilterMenu, closeRowMenu, closeUtilityMenu]);

  useEffect(() => {
    const handleViewportShift = () => {
      closeRowMenu();
      closeFilterMenu();
      closeUtilityMenu();
    };

    window.addEventListener("resize", handleViewportShift);
    window.addEventListener("scroll", handleViewportShift, true);
    return () => {
      window.removeEventListener("resize", handleViewportShift);
      window.removeEventListener("scroll", handleViewportShift, true);
    };
  }, [closeFilterMenu, closeRowMenu, closeUtilityMenu]);

  const visibleEntries = useMemo(
    () =>
      ledgerEntries
        .filter((entry) => !entry.archived)
        .filter((entry) => typeFilter.size === 0 || typeFilter.has(entry.type))
        .filter((entry) => ledgerEntryMatchesDateRange(entry, dateFilter))
        .filter(
          (entry) =>
            childFilter === "All children" || entry.studentName === childFilter,
        ),
    [childFilter, dateFilter, ledgerEntries, typeFilter],
  );

  const planLabel =
    family?.planLabel ??
    (family
      ? family.netBalanceCents <= 0
        ? "Annual"
        : family.studentCount > 2
          ? "10-month"
          : family.autoPayEnrolled
            ? "Semi-annual"
            : "Annual"
      : "Annual");
  const activeAcademicYear = ledgerSnapshot?.academicYear ?? ACADEMIC_YEAR;

  const archivedCount = ledgerEntries.filter((entry) => entry.archived).length;

  const runAction = async (actionId: string, action: () => Promise<string>) => {
    setBusyAction(actionId);
    setActionError(null);
    setActionFeedback(null);
    try {
      setActionFeedback(await action());
    } catch (actionFailure) {
      setActionError(
        actionFailure instanceof Error
          ? actionFailure.message
          : "Tuition action failed.",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const handleSaveEditedEntry = () => {
    setLedgerEntries((current) =>
      current.map((entry) => {
        if (entry.id !== editingId) {
          return entry;
        }
        const nextType = editFormData.type ?? entry.type;
        const absoluteAmount = Math.abs(
          Number(editFormData.amount ?? Math.abs(entry.amountCents / 100)),
        );
        const isNegative = nextType === "Payment" || nextType === "Credit";
        return {
          ...entry,
          date: editFormData.date ?? entry.date,
          description: editFormData.description ?? entry.description,
          type: nextType,
          amountCents: Math.round(
            (isNegative ? -absoluteAmount : absoluteAmount) * 100,
          ),
          source: editFormData.source ?? entry.source,
          studentName: editFormData.studentName ?? entry.studentName,
        };
      }),
    );
    setEditingId(null);
  };

  const handleRecordPayment = async () => {
    if (!family) {
      throw new Error("No family is selected.");
    }
    const amountCents = Math.round(Number(recordPaymentForm.amount) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      throw new Error("Payment amount must be greater than zero.");
    }

    if (snapshotOverride != null) {
      const studentName =
        recordPaymentForm.studentId !== "all"
          ? (family.students.find(
              (student) => student.id === recordPaymentForm.studentId,
            )?.name ?? null)
          : null;
      setLedgerEntries((current) => {
        const latestBalance =
          current[current.length - 1]?.runningBalanceCents ??
          family.netBalanceCents;
        const nextAmountCents = -amountCents;
        return [
          ...current,
          {
            id: `storybook-payment-${Date.now()}`,
            date: recordPaymentForm.receivedDate,
            description: `${recordPaymentForm.method} payment${recordPaymentForm.period ? ` • ${recordPaymentForm.period}` : ""}`,
            type: "Payment",
            entryType: "payment",
            amountCents: nextAmountCents,
            runningBalanceCents: latestBalance + nextAmountCents,
            source:
              recordPaymentForm.method === "Check" ? "external" : "intellio",
            sourceLabel:
              recordPaymentForm.method === "Check"
                ? recordPaymentForm.referenceCode.trim()
                  ? `Check #${recordPaymentForm.referenceCode.trim()}`
                  : "Manual check"
                : recordPaymentForm.method === "Cash"
                  ? "Cash"
                  : recordPaymentForm.method === "ACH"
                    ? recordPaymentForm.referenceCode.trim()
                      ? `ACH ${recordPaymentForm.referenceCode.trim()}`
                      : "ACH"
                    : recordPaymentForm.method === "Wire"
                      ? recordPaymentForm.referenceCode.trim()
                        ? `Wire ${recordPaymentForm.referenceCode.trim()}`
                        : "Wire"
                      : recordPaymentForm.method === "Manual card capture"
                        ? recordPaymentForm.referenceCode.trim()
                          ? `Manual card ${recordPaymentForm.referenceCode.trim()}`
                          : "Manual card capture"
                      : recordPaymentForm.referenceCode.trim()
                        ? `Manual ref ${recordPaymentForm.referenceCode.trim()}`
                        : "Manual other",
            studentName,
            relatedEntryId: null,
            disputeId: null,
            disputeDeadline: null,
            isReversed: false,
            billingCategory: null,
            adhocInvoiceId: null,
          },
        ];
      });
      closeRecordPaymentModal();
      return "Manual payment recorded on the family ledger.";
    }

    await domainRouterClient.mutate("createFamilyBillingEntry", {
      input: {
        familyId: family.familyId,
        studentId:
          recordPaymentForm.studentId && recordPaymentForm.studentId !== "all"
            ? recordPaymentForm.studentId
            : family.students[0]?.id,
        academicYear: ledgerSnapshot?.academicYear ?? ACADEMIC_YEAR,
        entryType: "payment",
        amount: -amountCents,
        description: `${recordPaymentForm.method} payment${recordPaymentForm.period ? ` • ${recordPaymentForm.period}` : ""}${recordPaymentForm.notes.trim() ? ` • ${recordPaymentForm.notes.trim()}` : ""}`,
        referenceCode:
          recordPaymentForm.referenceCode.trim() || `MANUAL-${Date.now()}`,
        status: "posted",
        effectiveDate: recordPaymentForm.receivedDate,
        source:
          recordPaymentForm.method === "Check"
            ? "manual_check"
            : recordPaymentForm.method === "Cash"
              ? "manual_cash"
              : recordPaymentForm.method === "ACH"
                ? "manual_ach"
                : recordPaymentForm.method === "Wire"
                  ? "manual_wire"
                  : recordPaymentForm.method === "Manual card capture"
                    ? "manual_card"
                  : "manual_other",
      },
    });

    await familyLedgerQuery.refetch();
    closeRecordPaymentModal();
    return "Manual payment recorded on the family ledger.";
  };

  const handleOpenPaymentProcessView = () => {
    const amount = Number(recordPaymentForm.amount);
    const isValid =
      recordPaymentForm.studentId !== "" &&
      recordPaymentForm.period !== "" &&
      Number.isFinite(amount) &&
      amount > 0;
    if (!isValid) {
      return;
    }
    setRecordPaymentProcessDraft({ ...recordPaymentForm });
    setRecordPaymentModalView("process");
  };

  const handleReversePayment = async () => {
    if (!family || !selectedReverseEntry) {
      return;
    }

    try {
      await reverseTuitionPayment({
        ledgerEntryId: selectedReverseEntry.id,
        reason: reversePaymentForm.reason.trim(),
        tuitionAccountId: family.familyId,
      }).unwrap();
      await familyLedgerQuery.refetch();
      setActionFeedback(
        `Payment of ${formatCurrencyDollars(Math.abs(selectedReverseEntry.amountCents) / 100)} reversed for ${family.familyName}.`,
      );
      setActionError(null);
      setReversePaymentForm(buildReversePaymentDefaults());
      setSelectedReverseEntry(null);
      setIsReverseModalOpen(false);
    } catch (error) {
      setReversePaymentForm((current) => ({
        ...current,
        error:
          error && typeof error === "object" && "message" in error
            ? String(error.message)
            : "Unable to reverse this payment.",
      }));
    }
  };

  const handleIssueRefund = async () => {
    if (!family) {
      return;
    }

    try {
      await issueTuitionRefund({
        tuitionAccountId: family.familyId,
        amount: Number(issueRefundForm.amount),
        reason: issueRefundForm.reason,
        reasonNotes:
          issueRefundForm.reason === "OTHER"
            ? issueRefundForm.reasonNotes.trim()
            : undefined,
        method: issueRefundForm.method,
        originalPaymentEntryId:
          issueRefundForm.method === "STRIPE_ORIGINAL"
            ? issueRefundForm.originalPaymentEntryId
            : undefined,
        referenceNumber:
          issueRefundForm.method === "STRIPE_ORIGINAL"
            ? undefined
            : issueRefundForm.referenceNumber.trim() || undefined,
      }).unwrap();
      await familyLedgerQuery.refetch();
      setActionFeedback(
        `Refund of ${formatCurrencyDollars(Number(issueRefundForm.amount || 0))} issued for ${family.familyName}.`,
      );
      setActionError(null);
      setIssueRefundForm(buildIssueRefundDefaults());
      setIsRefundModalOpen(false);
    } catch (error) {
      setIssueRefundForm((current) => ({
        ...current,
        error: extractTuitionActionErrorMessage(
          error,
          "Unable to issue this refund.",
        ),
      }));
    }
  };

  const handleAddCharge = async () => {
    if (!family) {
      return;
    }

    try {
      await createAdHocCharge({
        tuitionAccountId: family.familyId,
        chargeName: addChargeForm.chargeName.trim(),
        amount: Number(addChargeForm.amount),
        perStudent: addChargeForm.perStudent,
        studentIds: addChargeForm.perStudent
          ? addChargeForm.studentIds
          : undefined,
        dueDate: addChargeForm.dueDate,
        billingCategory: addChargeForm.billingCategory,
        notes: addChargeForm.notes.trim() || undefined,
      }).unwrap();
      await familyLedgerQuery.refetch();
      setActionFeedback(
        `Charge of ${formatCurrencyDollars(Number(addChargeForm.amount || 0))} added for ${family.familyName}.`,
      );
      setActionError(null);
      setAddChargeForm(buildAddChargeDefaults());
      setIsAddChargeModalOpen(false);
    } catch (error) {
      setAddChargeForm((current) => ({
        ...current,
        error:
          error && typeof error === "object" && "message" in error
            ? String(error.message)
            : "Unable to add this charge.",
      }));
    }
  };

  const handleInvoiceDownload = async (entry: SchoolLedgerEntry) => {
    if (!family) {
      return;
    }
    await openTextPdfInNewTab({
      title: `${family.familyName} invoice`,
      fileName: `${normalizeFileSegment(family.familyName)}-invoice-${entry.id}.pdf`,
      lines: [
        "Yeshiva School of Technology NJ",
        "",
        `Family: ${family.familyName}`,
        `Date: ${formatDate(entry.date)}`,
        `Description: ${entry.description}`,
        `Type: ${entry.type}`,
        `Amount: ${formatCurrencyDollars(Math.abs(entry.amountCents) / 100)}`,
        `Running balance: ${formatCurrencyDollars(Math.abs(entry.runningBalanceCents) / 100)}`,
      ],
    });
    setActiveMenuId(null);
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!family) {
    return (
      <div className="min-h-full bg-background-light p-6 font-body">
        <div className="mx-auto max-w-[960px] space-y-4">
          <TuitionToastStack toasts={toasts} onDismiss={dismissToast} />
          <BackToDashboardLink />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="school-tuition-family-ledger-route"
      data-route-family="internal-owned"
      data-route-ready="true"
      className="min-h-full bg-background-light p-6 font-body"
    >
      <TuitionToastStack toasts={toasts} onDismiss={dismissToast} />
      <div
        className="mx-auto max-w-[1440px] space-y-6"
        style={{ marginTop: "-84px" }}
      >
        <BackToDashboardLink />

        {activeChargebackEntry ? (
          <ChargebackAlertBanner entry={activeChargebackEntry} />
        ) : null}

        <div className="rounded-[12px] border border-border bg-surface-light p-6 shadow-sm">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-start">
            <div>
              <h1 className="font-heading text-2xl font-semibold text-iron-grey">
                {family.familyName} Family
              </h1>
              <p className="mt-1 font-body text-sm text-text-secondary">
                Academic Year {activeAcademicYear} • {family.studentCount}{" "}
                Students • {planLabel} Plan
              </p>
            </div>

            <div className="flex flex-wrap gap-6">
              <div>
                <p className="mb-1 font-body text-xs uppercase tracking-wider text-text-secondary">
                  Total billed
                </p>
                <p className="pt-[4px] font-body text-xl font-semibold text-iron-grey">
                  {formatCurrencyDollars(family.totalChargesCents / 100)}
                </p>
              </div>
              <div>
                <p className="mb-1 font-body text-xs uppercase tracking-wider text-text-secondary">
                  Total paid
                </p>
                <p className="pt-[4px] font-body text-xl font-semibold text-jungle">
                  {formatCurrencyDollars(family.totalCreditsCents / 100)}
                </p>
              </div>
              <div>
                <p className="mb-1 font-body text-xs uppercase tracking-wider text-text-secondary">
                  Current balance
                </p>
                <p className="font-body text-2xl font-bold text-iron-grey">
                  {formatCurrencyDollars(family.netBalanceCents / 100)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[12px] border border-border bg-surface-light shadow-sm">
          <div className="flex flex-col justify-between gap-4 border-b border-border bg-muted/30 p-4 sm:flex-row sm:items-center">
            <div
              ref={filterContainerRef}
              className="flex flex-wrap items-center gap-3"
            >
              <h2 className="font-title text-lg font-semibold text-iron-grey">
                Ledger
              </h2>
              <div className="mx-1 hidden h-5 w-px bg-border sm:block" />

              <div className="relative">
                <button
                  type="button"
                  onClick={(event) => {
                    const nextMenu =
                      activeFilterMenu === "type" ? null : "type";
                    setActiveFilterMenu(nextMenu);
                    setActiveFilterMenuPosition(
                      nextMenu
                        ? buildFixedLayerPosition(event.currentTarget, {
                            align: "left",
                            minWidth: 192,
                          })
                        : null,
                    );
                  }}
                  className={`flex items-center gap-2 rounded-[6px] border px-3 py-1.5 font-body text-[13px] font-medium transition-colors ${
                    typeFilter.size > 0
                      ? "border-primary text-primary"
                      : "border-border bg-surface-light text-iron-grey hover:bg-muted"
                  }`}
                >
                  <FilterIcon className="h-4 w-4 text-text-secondary" />
                  {typeFilter.size > 0
                    ? `${typeFilter.size} type${typeFilter.size > 1 ? "s" : ""}`
                    : "All types"}
                  <ChevronDownIcon className="h-3.5 w-3.5 text-text-secondary" />
                </button>
                {activeFilterMenu === "type" ? (
                  <FixedLayerPortalMenu
                    position={activeFilterMenuPosition}
                    portalRef={filterMenuPortalRef}
                    className="z-[130] w-48 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg"
                  >
                    {(
                      [
                        "Charge",
                        "Payment",
                        "Refund",
                        "Reversal",
                        "Chargeback",
                        "Credit",
                        "Late fee",
                        "Adjustment",
                      ] as SchoolLedgerType[]
                    ).map((type) => (
                      <label
                        key={type}
                        className="flex cursor-pointer items-center gap-2 px-4 py-2 font-body text-[13px] text-iron-grey hover:bg-muted"
                      >
                        <input
                          type="checkbox"
                          checked={typeFilter.has(type)}
                          onChange={() => {
                            const next = new Set(typeFilter);
                            if (next.has(type)) {
                              next.delete(type);
                            } else {
                              next.add(type);
                            }
                            setTypeFilter(next);
                          }}
                          className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                        />
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getLedgerTypeBadgeColor(type)}`}
                        >
                          {type}
                        </span>
                      </label>
                    ))}
                    {typeFilter.size > 0 ? (
                      <>
                        <div className="my-1 h-px bg-border" />
                        <button
                          type="button"
                          onClick={() => setTypeFilter(new Set())}
                          className="w-full px-4 py-2 text-left font-body text-[13px] text-text-secondary transition-colors hover:bg-muted hover:text-iron-grey"
                        >
                          Clear filters
                        </button>
                      </>
                    ) : null}
                  </FixedLayerPortalMenu>
                ) : null}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={(event) => {
                    const nextMenu =
                      activeFilterMenu === "date" ? null : "date";
                    setActiveFilterMenu(nextMenu);
                    setActiveFilterMenuPosition(
                      nextMenu
                        ? buildFixedLayerPosition(event.currentTarget, {
                            align: "left",
                            minWidth: 176,
                          })
                        : null,
                    );
                  }}
                  className={`flex items-center gap-2 rounded-[6px] border px-3 py-1.5 font-body text-[13px] font-medium transition-colors ${
                    dateFilter !== "all"
                      ? "border-primary text-primary"
                      : "border-border bg-surface-light text-iron-grey hover:bg-muted"
                  }`}
                >
                  <CalendarIcon className="h-4 w-4 text-text-secondary" />
                  {getLedgerDateRangeLabel(dateFilter)}
                  <ChevronDownIcon className="h-3.5 w-3.5 text-text-secondary" />
                </button>
                {activeFilterMenu === "date" ? (
                  <FixedLayerPortalMenu
                    position={activeFilterMenuPosition}
                    portalRef={filterMenuPortalRef}
                    className="z-[130] w-44 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg"
                  >
                    {(
                      [
                        "all",
                        "this-month",
                        "last-month",
                        "last-3-months",
                      ] as SchoolLedgerDateRange[]
                    ).map((range) => (
                      <button
                        key={range}
                        type="button"
                        onClick={() => {
                          setDateFilter(range);
                          closeFilterMenu();
                        }}
                        className={`w-full px-4 py-2 text-left font-body text-[13px] transition-colors hover:bg-muted ${
                          dateFilter === range
                            ? "font-semibold text-primary"
                            : "text-iron-grey"
                        }`}
                      >
                        {getLedgerDateRangeLabel(range)}
                      </button>
                    ))}
                  </FixedLayerPortalMenu>
                ) : null}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={(event) => {
                    const nextMenu =
                      activeFilterMenu === "child" ? null : "child";
                    setActiveFilterMenu(nextMenu);
                    setActiveFilterMenuPosition(
                      nextMenu
                        ? buildFixedLayerPosition(event.currentTarget, {
                            align: "left",
                            minWidth: 192,
                          })
                        : null,
                    );
                  }}
                  className={`flex items-center gap-2 rounded-[6px] border px-3 py-1.5 font-body text-[13px] font-medium transition-colors ${
                    childFilter !== "All children"
                      ? "border-primary text-primary"
                      : "border-border bg-surface-light text-iron-grey hover:bg-muted"
                  }`}
                >
                  <UserIcon className="h-4 w-4 text-text-secondary" />
                  <span className="max-w-[120px] truncate">{childFilter}</span>
                  <ChevronDownIcon className="h-3.5 w-3.5 text-text-secondary" />
                </button>
                {activeFilterMenu === "child" ? (
                  <FixedLayerPortalMenu
                    position={activeFilterMenuPosition}
                    portalRef={filterMenuPortalRef}
                    className="z-[130] w-48 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setChildFilter("All children");
                        closeFilterMenu();
                      }}
                      className={`w-full px-4 py-2 text-left font-body text-[13px] transition-colors hover:bg-muted ${
                        childFilter === "All children"
                          ? "font-semibold text-primary"
                          : "text-iron-grey"
                      }`}
                    >
                      All children
                    </button>
                    <div className="my-1 h-px bg-border" />
                    {Array.from(
                      new Set(
                        ledgerEntries
                          .filter(
                            (entry) => !entry.archived && entry.studentName,
                          )
                          .map((entry) => entry.studentName as string),
                      ),
                    ).map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          setChildFilter(name);
                          closeFilterMenu();
                        }}
                        className={`w-full px-4 py-2 text-left font-body text-[13px] transition-colors hover:bg-muted ${
                          childFilter === name
                            ? "font-semibold text-primary"
                            : "text-iron-grey"
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </FixedLayerPortalMenu>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setIsPayorsModalOpen(true)}
                data-testid="school-tuition-family-ledger-payors-button"
                className="flex items-center justify-center gap-2 rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted"
              >
                <UsersIcon className="h-4 w-4 text-text-secondary" />
                Payors
              </button>
              <div ref={utilityMenuRef} className="relative">
                <button
                  type="button"
                  onClick={(event) => {
                    const nextOpen = !showUtilityActions;
                    setShowUtilityActions(nextOpen);
                    setUtilityMenuPosition(
                      nextOpen
                        ? buildFixedLayerPosition(event.currentTarget, {
                            align: "right",
                            minWidth: 208,
                          })
                        : null,
                    );
                  }}
                  data-testid="school-tuition-family-ledger-more-actions-button"
                  aria-label="More actions"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-[6px] border border-border bg-surface-light text-text-secondary shadow-sm transition-colors hover:bg-muted hover:text-iron-grey"
                >
                  <MoreVerticalIcon className="h-4 w-4" />
                </button>
                {showUtilityActions ? (
                  <FixedLayerPortalMenu
                    position={utilityMenuPosition}
                    portalRef={utilityMenuPortalRef}
                    className="z-[130] w-52 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        closeUtilityMenu();
                        setIsRecurringFeeModalOpen(true);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                    >
                      <UsersRoundIcon className="h-4 w-4 text-text-secondary" />
                      Manage recurring fees
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        closeUtilityMenu();
                        setAddChargeForm(buildAddChargeDefaults());
                        setIsAddChargeModalOpen(true);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                    >
                      <DollarSignIcon className="h-4 w-4 text-text-secondary" />
                      Add charge
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        closeUtilityMenu();
                        setIssueRefundForm(buildIssueRefundDefaults());
                        setIsRefundModalOpen(true);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                    >
                      <DollarSignIcon className="h-4 w-4 text-text-secondary" />
                      Issue refund
                    </button>
                    <div className="my-1 h-px bg-border" />
                    <button
                      type="button"
                      onClick={() => {
                        closeUtilityMenu();
                        if (!family) {
                          return;
                        }
                        void runAction("family-ledger-export", async () => {
                          triggerDownload(
                            new Blob([buildAccountingExportCsv(family)], {
                              type: "text/csv;charset=utf-8",
                            }),
                            `${normalizeFileSegment(family.familyName)}-tuition-export.csv`,
                          );
                          return "Accounting export downloaded for this family.";
                        });
                      }}
                      data-testid="school-tuition-family-ledger-export-button"
                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                    >
                      <DownloadIcon className="h-4 w-4 text-text-secondary" />
                      Export ledger
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        closeUtilityMenu();
                        void runAction("family-ledger-receipt", async () => {
                          triggerDownload(
                            new Blob([buildReceiptArtifactBody(family)], {
                              type: "text/plain;charset=utf-8",
                            }),
                            `${normalizeFileSegment(family.familyName)}-receipt.txt`,
                          );
                          return "Receipt artifact prepared for this family.";
                        });
                      }}
                      data-testid="school-tuition-family-ledger-receipt-button"
                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                    >
                      <FileTextIcon className="h-4 w-4 text-text-secondary" />
                      Receipt
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        closeUtilityMenu();
                        void runAction("family-ledger-contract", async () => {
                          triggerDownload(
                            new Blob([buildContractArtifactBody(family)], {
                              type: "text/plain;charset=utf-8",
                            }),
                            `${normalizeFileSegment(family.familyName)}-contract.txt`,
                          );
                          return "Contract artifact prepared for this family.";
                        });
                      }}
                      data-testid="school-tuition-family-ledger-contract-button"
                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                    >
                      <FileTextIcon className="h-4 w-4 text-text-secondary" />
                      Contract
                    </button>
                  </FixedLayerPortalMenu>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setIsPaymentModalOpen(true)}
                data-testid="school-tuition-family-ledger-record-payment-button"
                className="flex items-center justify-center gap-2 rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
              >
                <DollarSignIcon className="h-4 w-4" />
                Record payment
              </button>
            </div>
          </div>

          {archivedCount > 0 ? (
            <div className="flex items-center justify-between border-b border-border bg-info-background px-4 py-2.5">
              <div className="flex items-center gap-2">
                <ArchiveIcon className="h-4 w-4 text-primary" />
                <span className="font-body text-[13px] text-iron-grey">
                  {archivedCount} archived{" "}
                  {archivedCount === 1 ? "entry" : "entries"} hidden
                </span>
              </div>
              <button
                type="button"
                onClick={() =>
                  setLedgerEntries((current) =>
                    current.map((entry) => ({ ...entry, archived: false })),
                  )
                }
                className="font-body text-[13px] font-medium text-primary transition-colors hover:text-primary-hover"
              >
                Restore all
              </button>
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Date
                  </th>
                  <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Description
                  </th>
                  <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Student
                  </th>
                  <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Type
                  </th>
                  <th className="px-6 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Running balance
                  </th>
                  <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Source
                  </th>
                  <th className="w-16 px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleEntries.map((entry) => {
                  const isEditing = editingId === entry.id;
                  const isNegative = entry.amountCents < 0;
                  const amountColor = isNegative
                    ? "text-jungle"
                    : "text-iron-grey";

                  if (isEditing) {
                    return (
                      <tr key={entry.id} className="bg-muted/30">
                        <td className="px-6 py-4">
                          <TailwindDateField
                            value={String(editFormData.date ?? "")}
                            onChange={(event) =>
                              setEditFormData((current) => ({
                                ...current,
                                date: event.target.value,
                              }))
                            }
                            className="w-full rounded-[6px] border border-border bg-surface-light px-2 py-1.5 font-body text-sm text-iron-grey focus:border-primary focus:outline-none"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="text"
                            value={String(editFormData.description ?? "")}
                            onChange={(event) =>
                              setEditFormData((current) => ({
                                ...current,
                                description: event.target.value,
                              }))
                            }
                            className="w-full rounded-[6px] border border-border bg-surface-light px-2 py-1.5 font-body text-sm text-iron-grey focus:border-primary focus:outline-none"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={String(editFormData.studentName ?? "")}
                            onChange={(event) =>
                              setEditFormData((current) => ({
                                ...current,
                                studentName: event.target.value || undefined,
                              }))
                            }
                            className="w-full rounded-[6px] border border-border bg-surface-light px-2 py-1.5 font-body text-sm text-iron-grey focus:border-primary focus:outline-none"
                          >
                            <option value="">Family (None)</option>
                            {family.studentNames.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={String(editFormData.type ?? "")}
                            onChange={(event) =>
                              setEditFormData((current) => ({
                                ...current,
                                type: event.target.value as SchoolLedgerType,
                              }))
                            }
                            className="w-full rounded-[6px] border border-border bg-surface-light px-2 py-1.5 font-body text-sm text-iron-grey focus:border-primary focus:outline-none"
                          >
                            <option value="Charge">Charge</option>
                            <option value="Payment">Payment</option>
                            <option value="Refund">Refund</option>
                            <option value="Reversal">Reversal</option>
                            <option value="Chargeback">Chargeback</option>
                            <option value="Credit">Credit</option>
                            <option value="Late fee">Late fee</option>
                            <option value="Adjustment">Adjustment</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="number"
                            value={String(editFormData.amount ?? "")}
                            onChange={(event) =>
                              setEditFormData((current) => ({
                                ...current,
                                amount: Number(event.target.value) || 0,
                              }))
                            }
                            className="w-full rounded-[6px] border border-border bg-surface-light px-2 py-1.5 text-right font-body text-sm text-iron-grey focus:border-primary focus:outline-none"
                          />
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-right font-body text-sm font-semibold text-iron-grey">
                          {formatCurrencyDollars(
                            entry.runningBalanceCents / 100,
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={String(editFormData.source ?? "")}
                            onChange={(event) =>
                              setEditFormData((current) => ({
                                ...current,
                                source: event.target
                                  .value as SchoolLedgerEntry["source"],
                              }))
                            }
                            className="w-full rounded-[6px] border border-border bg-surface-light px-2 py-1.5 font-body text-sm text-iron-grey focus:border-primary focus:outline-none"
                          >
                            <option value="intellio">Intellio</option>
                            <option value="external">External</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={handleSaveEditedEntry}
                              className="rounded-[6px] bg-success-background p-1.5 text-jungle transition-colors hover:bg-aquamarine/20"
                              title="Save"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="rounded-[6px] bg-error-background p-1.5 text-strawberry transition-colors hover:bg-coral/20"
                              title="Cancel"
                            >
                              <XIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr
                      key={entry.id}
                      className="transition-colors hover:bg-[#fbf9fa]"
                    >
                      <td className="whitespace-nowrap px-6 py-4 font-body text-sm text-iron-grey">
                        {formatDate(entry.date)}
                      </td>
                      <td className="px-6 py-4 font-body text-sm text-iron-grey">
                        <div className="space-y-1">
                          <div>{entry.description}</div>
                          {entry.isReversed ? (
                            <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-text-secondary">
                              Reversed
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-body text-sm text-text-secondary">
                        {entry.studentName || "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${getLedgerTypeBadgeColor(entry.type)}`}
                        >
                          {entry.type}
                        </span>
                      </td>
                      <td
                        className={`whitespace-nowrap px-6 py-4 text-right font-body text-sm font-medium ${amountColor}`}
                      >
                        {isNegative ? "-" : ""}
                        {formatCurrencyDollars(
                          Math.abs(entry.amountCents) / 100,
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right font-body text-sm font-semibold text-iron-grey">
                        {formatCurrencyDollars(entry.runningBalanceCents / 100)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {entry.source === "intellio" ? (
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary"
                            title={entry.sourceLabel}
                          >
                            <span className="h-3 w-3 rounded-sm bg-primary" />
                          </span>
                        ) : (
                          <span
                            className="inline-flex h-6 w-6 items-center justify-center text-text-secondary"
                            title={entry.sourceLabel}
                          >
                            <span className="font-body text-[10px] font-bold">
                              EXT
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="relative px-6 py-4 text-center">
                        <button
                          type="button"
                          onClick={(event) => {
                            const nextMenuId =
                              activeMenuId === entry.id ? null : entry.id;
                            setActiveMenuId(nextMenuId);
                            setActiveMenuPosition(
                              nextMenuId
                                ? buildFixedLayerPosition(event.currentTarget, {
                                    align: "right",
                                    minWidth: 192,
                                  })
                                : null,
                            );
                          }}
                          className="mx-auto inline-flex items-center justify-center rounded-[6px] p-1.5 text-text-secondary transition-colors hover:bg-border/50 hover:text-iron-grey"
                        >
                          <MoreVerticalIcon className="h-4 w-4" />
                        </button>

                        {activeMenuId === entry.id ? (
                          <FixedLayerPortalMenu
                            position={activeMenuPosition}
                            portalRef={menuRef}
                            className="z-[130] w-48 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg"
                          >
                            <button
                              type="button"
                              onClick={() => {
                                void handleInvoiceDownload(entry);
                              }}
                              className="flex w-full items-center gap-2.5 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                            >
                              <FileTextIcon className="h-4 w-4 text-text-secondary" />
                              Invoice PDF
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId(entry.id);
                                setEditFormData({
                                  date: entry.date,
                                  description: entry.description,
                                  type: entry.type,
                                  amount: Math.abs(entry.amountCents / 100),
                                  source: entry.source,
                                  studentName: entry.studentName ?? undefined,
                                });
                                closeRowMenu();
                              }}
                              className="flex w-full items-center gap-2.5 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                            >
                              <PencilIcon className="h-4 w-4 text-text-secondary" />
                              Update record
                            </button>
                            {entry.entryType === "payment" &&
                            !entry.isReversed ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedReverseEntry(entry);
                                  setReversePaymentForm(
                                    buildReversePaymentDefaults(),
                                  );
                                  setIsReverseModalOpen(true);
                                  closeRowMenu();
                                }}
                                className="flex w-full items-center gap-2.5 px-4 py-2 text-left font-body text-[13px] text-strawberry transition-colors hover:bg-error-background"
                              >
                                <XCircleIcon className="h-4 w-4" />
                                Reverse
                              </button>
                            ) : null}
                            <div className="my-1 h-px bg-border" />
                            <button
                              type="button"
                              onClick={() => {
                                setLedgerEntries((current) =>
                                  current.map((candidate) =>
                                    candidate.id === entry.id
                                      ? { ...candidate, archived: true }
                                      : candidate,
                                  ),
                                );
                                closeRowMenu();
                              }}
                              className="flex w-full items-center gap-2.5 px-4 py-2 text-left font-body text-[13px] text-strawberry transition-colors hover:bg-error-background"
                            >
                              <ArchiveIcon className="h-4 w-4" />
                              Archive
                            </button>
                          </FixedLayerPortalMenu>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}

                {visibleEntries.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <p className="font-body text-sm text-text-secondary">
                        No ledger entries match the current filters.
                      </p>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <RecordManualPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={closeRecordPaymentModal}
        family={family}
        form={recordPaymentForm}
        processDraft={recordPaymentProcessDraft}
        view={recordPaymentModalView}
        onChange={(patch) =>
          setRecordPaymentForm((current) => ({ ...current, ...patch }))
        }
        onProcessPayment={handleOpenPaymentProcessView}
        onBackToEntry={() => setRecordPaymentModalView("entry")}
        onSubmit={() => {
          void runAction("family-ledger-record-payment", handleRecordPayment);
        }}
        busy={busyAction === "family-ledger-record-payment"}
      />

      <ManagePayorsModal
        isOpen={isPayorsModalOpen}
        onClose={() => setIsPayorsModalOpen(false)}
        familyName={family.familyName}
        payers={localPayers}
        draft={payorDraft}
        onDraftChange={(patch) =>
          setPayorDraft((current) => ({ ...current, ...patch }))
        }
        onSave={() => {
          const displayName =
            `${payorDraft.firstName} ${payorDraft.lastName}`.trim();
          if (!displayName) {
            return;
          }
          setLocalPayers((current) => [
            ...current,
            {
              id: `local-payer-${Date.now()}`,
              holderType: "third_party",
              displayName,
              relationLabel: payorDraft.relationLabel,
              email: payorDraft.email || null,
              isPrimary: false,
              allocationPercent:
                payorDraft.contributionType === "%"
                  ? Number(payorDraft.contributionValue) || 0
                  : 0,
              allocationBalanceCents:
                payorDraft.contributionType === "$"
                  ? Math.round(Number(payorDraft.contributionValue || 0) * 100)
                  : Math.round(
                      (family.netBalanceCents *
                        (Number(payorDraft.contributionValue) || 0)) /
                        100,
                    ),
            },
          ]);
          setPayorDraft(buildPayorFormDefaults());
          setIsPayorsModalOpen(false);
        }}
      />

      <ReversePaymentModal
        isOpen={isReverseModalOpen}
        onClose={() => {
          setIsReverseModalOpen(false);
          setSelectedReverseEntry(null);
          setReversePaymentForm(buildReversePaymentDefaults());
        }}
        family={family}
        paymentEntry={selectedReverseEntry}
        form={reversePaymentForm}
        onChange={(patch) =>
          setReversePaymentForm((current) => ({ ...current, ...patch }))
        }
        onSubmit={() => {
          void handleReversePayment();
        }}
        busy={reverseTuitionPaymentState.isLoading}
      />

      <IssueRefundModal
        isOpen={isRefundModalOpen}
        onClose={() => {
          setIsRefundModalOpen(false);
          setIssueRefundForm(buildIssueRefundDefaults());
        }}
        family={family}
        form={issueRefundForm}
        onChange={(patch) =>
          setIssueRefundForm((current) => ({ ...current, ...patch }))
        }
        onSubmit={() => {
          void handleIssueRefund();
        }}
        busy={issueTuitionRefundState.isLoading}
        maxRefundableCents={maxRefundableCents}
        stripeEligiblePayments={eligibleStripePayments}
      />

      <AddChargeModal
        isOpen={isAddChargeModalOpen}
        onClose={() => {
          setIsAddChargeModalOpen(false);
          setAddChargeForm(buildAddChargeDefaults());
        }}
        family={family}
        form={addChargeForm}
        onChange={(patch) =>
          setAddChargeForm((current) => ({ ...current, ...patch }))
        }
        onSubmit={() => {
          void handleAddCharge();
        }}
        busy={createAdHocChargeState.isLoading}
      />

      <RecurringFeeAssignment
        isOpen={isRecurringFeeModalOpen}
        onClose={() => setIsRecurringFeeModalOpen(false)}
        family={family}
        onSaved={(message) => {
          setActionFeedback(message);
          setActionError(null);
        }}
      />
    </div>
  );
}

type SchoolTuitionOwnedPageProps = {
  snapshotOverride?: TuitionDashboardSnapshot | null;
  initialError?: string | null;
  suppressSelectedFamilyDetails?: boolean;
  initialFamilyLedgerOverlay?: FamilyLedgerInitialOverlay;
};

export function SchoolTuitionOwnedPage({
  snapshotOverride = null,
  initialError = null,
  suppressSelectedFamilyDetails: _suppressSelectedFamilyDetails = false,
}: SchoolTuitionOwnedPageProps) {
  const navigate = useNavigate();
  const [createAdHocCharge, createAdHocChargeState] =
    useCreateAdHocChargeMutation();
  const [createBulkCharge, createBulkChargeState] =
    useCreateBulkChargeMutation();
  const [previewBulkChargeSegment, previewBulkChargeSegmentState] =
    useLazyPreviewBulkChargeSegmentQuery();
  const [getBulkChargeJob] = useLazyGetBulkChargeJobQuery();
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { toasts, dismissToast, pushToast } = useTransientTuitionToasts();
  const [searchValue, setSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [activeFamilyTableFilterMenu, setActiveFamilyTableFilterMenu] =
    useState<"status" | "plan" | "auto-pay" | null>(null);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<
    Set<string>
  >(new Set());
  const [selectedPlanFilters, setSelectedPlanFilters] = useState<Set<string>>(
    new Set(),
  );
  const [selectedAutoPayFilters, setSelectedAutoPayFilters] = useState<
    Set<string>
  >(new Set());
  const [activeFamilyActionMenuId, setActiveFamilyActionMenuId] = useState<
    string | null
  >(null);
  const [showCollectionTrend, setShowCollectionTrend] = useState(true);
  const [collectionTrendPreset, setCollectionTrendPreset] =
    useState<CollectionTrendPreset>("12m");
  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState(ACADEMIC_YEAR);
  const [customRangeStart, setCustomRangeStart] = useState("");
  const [customRangeEnd, setCustomRangeEnd] = useState("");
  const [selectedChargeFamily, setSelectedChargeFamily] =
    useState<TuitionFamilySnapshot | null>(null);
  const [isAddChargeModalOpen, setIsAddChargeModalOpen] = useState(false);
  const [addChargeForm, setAddChargeForm] = useState<AddChargeFormState>(
    buildAddChargeDefaults(),
  );
  const [isBulkChargeModalOpen, setIsBulkChargeModalOpen] = useState(false);
  const [bulkChargeForm, setBulkChargeForm] = useState<BulkChargeFormState>(
    buildBulkChargeDefaults(),
  );
  const [activeBulkChargeJob, setActiveBulkChargeJob] =
    useState<BulkChargeJobSnapshot | null>(null);
  const exportMenuRef = useRef<HTMLDivElement | null>(null);
  const familyTableFiltersRef = useRef<HTMLDivElement | null>(null);
  const familyActionMenuRef = useRef<HTMLDivElement | null>(null);
  const deferredSearchValue = useDeferredValue(searchValue.trim());
  const dashboardQuery = useSchoolTuitionDashboardObjectTree({
    academicYear: ACADEMIC_YEAR,
    limit: 100,
    searchQuery: deferredSearchValue || undefined,
    skip: snapshotOverride != null,
  });
  const snapshot = snapshotOverride ?? dashboardQuery.snapshot;
  const loading = snapshotOverride == null ? dashboardQuery.isLoading : false;
  const error =
    initialError ?? (snapshotOverride == null ? dashboardQuery.error : null);
  const activeAcademicYear = snapshot?.academicYear ?? ACADEMIC_YEAR;

  useEffect(() => {
    setSelectedAcademicYear(activeAcademicYear);
  }, [activeAcademicYear]);

  useEffect(() => {
    if (!snapshot || (customRangeStart && customRangeEnd)) {
      return;
    }
    const anchorIsoDate = resolveAnchorIsoDate(snapshot);
    setCustomRangeStart(shiftIsoDate(anchorIsoDate, -364));
    setCustomRangeEnd(anchorIsoDate);
  }, [customRangeEnd, customRangeStart, snapshot]);

  useEffect(() => {
    if (!error) {
      return;
    }
    pushToast({
      message: error,
      variant: "error",
      testId: "school-tuition-dashboard-error",
    });
  }, [error, pushToast]);

  useEffect(() => {
    if (!actionError) {
      return;
    }
    pushToast({
      message: actionError,
      variant: "error",
      testId: "school-tuition-action-error",
    });
  }, [actionError, pushToast]);

  useEffect(() => {
    if (!actionFeedback) {
      return;
    }
    pushToast({
      message: actionFeedback,
      variant: "success",
      testId: "school-tuition-action-feedback",
    });
  }, [actionFeedback, pushToast]);

  useEffect(() => {
    if (loading || snapshot) {
      return;
    }
    pushToast({
      message:
        "No tuition records were returned for the active school dashboard.",
      variant: "error",
      testId: "school-tuition-dashboard-empty",
    });
  }, [loading, pushToast, snapshot]);

  const parsedSearch = useMemo(
    () => parseDashboardSearch(deferredSearchValue),
    [deferredSearchValue],
  );

  const filteredFamilies = useMemo(() => {
    const families = snapshot?.families ?? [];
    return families.filter((family) => {
      if (deferredSearchValue && !familyMatchesSearch(family, parsedSearch)) {
        return false;
      }

      const statusLabel = resolveTableStatusLabel(family);
      if (
        selectedStatusFilters.size > 0 &&
        !selectedStatusFilters.has(statusLabel)
      ) {
        return false;
      }

      const planLabel = resolveFamilyPlanLabel(family);
      if (selectedPlanFilters.size > 0 && !selectedPlanFilters.has(planLabel)) {
        return false;
      }

      const autoPayLabel = family.autoPayEnrolled ? "Enabled" : "Disabled";
      if (
        selectedAutoPayFilters.size > 0 &&
        !selectedAutoPayFilters.has(autoPayLabel)
      ) {
        return false;
      }

      return true;
    });
  }, [
    deferredSearchValue,
    parsedSearch,
    selectedAutoPayFilters,
    selectedPlanFilters,
    selectedStatusFilters,
    snapshot?.families,
  ]);

  const displayedFamilies = filteredFamilies;
  const statusFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (snapshot?.families ?? []).map((family) =>
            resolveTableStatusLabel(family),
          ),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [snapshot?.families],
  );
  const planFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (snapshot?.families ?? []).map((family) =>
            resolveFamilyPlanLabel(family),
          ),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [snapshot?.families],
  );
  const autoPayFilterOptions = ["Enabled", "Disabled"];
  const gradeOptions = ["Pre", "K", "1", "2", "3", "4", "5", "6", "7", "8"];
  const divisionOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (snapshot?.families ?? [])
            .flatMap((family) =>
              family.students.map((student) => student.divisionLabel),
            )
            .filter(Boolean),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [snapshot?.families],
  );
  const rateCategoryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (snapshot?.families ?? [])
            .flatMap((family) =>
              family.students.map((student) => student.rateCategory ?? ""),
            )
            .filter(Boolean),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [snapshot?.families],
  );
  const useServerDashboardAggregates =
    snapshot != null &&
    !deferredSearchValue &&
    displayedFamilies.length === snapshot.families.length;
  const dashboardRefetch = dashboardQuery.refetch;
  const anchorIsoDate = resolveAnchorIsoDate(snapshot);

  const viewSummary = useMemo(() => {
    if (snapshot != null && useServerDashboardAggregates) {
      return {
        totalBilledCents: snapshot.totalBilledCents,
        totalCollectedCents: snapshot.totalCollectedCents,
        outstandingCents: snapshot.outstandingCents,
        currentDueCents: snapshot.currentDueCents,
        overdueCents: snapshot.overdueCents,
        collectionRate: snapshot.collectionRate,
        hasCorrelatedStripeData: snapshot.hasCorrelatedStripeData,
        latestObservedAt: snapshot.latestObservedAt,
        dateRangeStart: snapshot.dateRangeStart,
        dateRangeEnd: snapshot.dateRangeEnd,
      };
    }

    const transactions = displayedFamilies.flatMap(
      (family) => family.transactions,
    );
    const totalBilledCents = displayedFamilies.reduce(
      (sum, family) => sum + family.totalChargesCents,
      0,
    );
    const totalCollectedCents = displayedFamilies.reduce(
      (sum, family) => sum + family.totalCreditsCents,
      0,
    );
    const outstandingCents = displayedFamilies.reduce(
      (sum, family) => sum + Math.max(family.netBalanceCents, 0),
      0,
    );
    const currentDueCents = displayedFamilies.reduce(
      (sum, family) => sum + Math.max(family.currentDueCents, 0),
      0,
    );
    const overdueCents = displayedFamilies.reduce(
      (sum, family) => sum + family.overdueCents,
      0,
    );
    const latestObservedAt =
      transactions
        .map((transaction) => transaction.observedAt)
        .filter((value): value is string => Boolean(value))
        .sort((left, right) => Date.parse(right) - Date.parse(left))[0] ?? null;
    const dateRange = transactions
      .map((transaction) => transaction.observedAt)
      .filter((value): value is string => Boolean(value))
      .sort((left, right) => Date.parse(left) - Date.parse(right));

    return {
      totalBilledCents,
      totalCollectedCents,
      outstandingCents,
      currentDueCents,
      overdueCents,
      collectionRate:
        totalBilledCents > 0 ? totalCollectedCents / totalBilledCents : 0,
      hasCorrelatedStripeData: transactions.some(
        (transaction) => transaction.isStripeCorrelated,
      ),
      latestObservedAt,
      dateRangeStart: dateRange[0] ?? null,
      dateRangeEnd: dateRange[dateRange.length - 1] ?? null,
    };
  }, [displayedFamilies, snapshot, useServerDashboardAggregates]);

  const generatedOnLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    [],
  );
  const dashboardExportPdfRows = useMemo(
    () =>
      displayedFamilies.map((family) => ({
        familyName: family.familyName,
        students: String(family.studentCount),
        annualAmount: formatCurrency(family.totalChargesCents),
        paid: formatCurrency(family.totalCreditsCents),
        balance: formatCurrency(Math.max(family.netBalanceCents, 0)),
        plan: resolveFamilyPlanLabel(family),
        status: resolveTableStatusLabel(family),
        autoPay: family.autoPayEnrolled ? "Yes" : "No",
        dueDate: resolveDueDateIndicatorText(family, anchorIsoDate).label,
      })),
    [anchorIsoDate, displayedFamilies],
  );
  const dashboardExportSummaryItems = useMemo(
    () => [
      {
        label: "Total billed",
        value: formatCurrency(viewSummary.totalBilledCents),
      },
      {
        label: "Collected",
        value: formatCurrency(viewSummary.totalCollectedCents),
      },
      {
        label: "Collection rate",
        value:
          viewSummary.totalBilledCents > 0
            ? formatPercent(viewSummary.collectionRate)
            : "0%",
      },
      {
        label: "Outstanding",
        value: formatCurrency(viewSummary.outstandingCents),
      },
    ],
    [
      viewSummary.collectionRate,
      viewSummary.outstandingCents,
      viewSummary.totalBilledCents,
      viewSummary.totalCollectedCents,
    ],
  );
  const emptyDashboardCopy = useMemo(() => {
    if (snapshot == null) {
      return null;
    }
    if (displayedFamilies.length === 0 && deferredSearchValue) {
      return "No family accounts matched the current search. Try Family Name, Student Name, Due date: today, or Status: Overdue.";
    }
    if (snapshot.emptyStateReason === "no-family-accounts") {
      return `No school tuition accounts are available for ${selectedAcademicYear} yet. Add family billing records before relying on chart or collection metrics.`;
    }
    if (
      viewSummary.totalBilledCents === 0 &&
      viewSummary.totalCollectedCents === 0 &&
      viewSummary.outstandingCents === 0
    ) {
      return `No tuition charges or settlement activity have been recorded for ${selectedAcademicYear} in the current school tuition view yet.`;
    }
    return null;
  }, [
    deferredSearchValue,
    displayedFamilies.length,
    selectedAcademicYear,
    snapshot,
    viewSummary.outstandingCents,
    viewSummary.totalBilledCents,
    viewSummary.totalCollectedCents,
  ]);
  const trendData = useMemo(
    () => snapshot == null
      ? []
      : resolveCollectionTrendProjection(
        snapshot,
        new Date().toISOString().slice(0, 10),
      ),
    [snapshot],
  );
  const currentDueCents = viewSummary.currentDueCents;
  const bulkChargePreview = (previewBulkChargeSegmentState.data ??
    null) as BulkChargePreviewSnapshot | null;
  const bulkChargePreviewErrorMessage = previewBulkChargeSegmentState.error
    ? resolveBulkChargePreviewErrorMessage(previewBulkChargeSegmentState.error)
    : null;
  const fallbackBulkChargePreview = useMemo(
    () =>
      buildFallbackBulkChargePreview(snapshot, {
        grades: bulkChargeForm.grades,
        divisions: bulkChargeForm.divisions,
        gender: bulkChargeForm.gender,
        rateCategories: bulkChargeForm.rateCategories,
      }),
    [
      bulkChargeForm.divisions,
      bulkChargeForm.gender,
      bulkChargeForm.grades,
      bulkChargeForm.rateCategories,
      snapshot,
    ],
  );
  const resolvedBulkChargePreview = previewBulkChargeSegmentState.isError
    ? fallbackBulkChargePreview
    : bulkChargePreview;
  const isBulkChargePreviewLoading =
    previewBulkChargeSegmentState.isUninitialized ||
    previewBulkChargeSegmentState.isFetching ||
    previewBulkChargeSegmentState.isLoading;
  const bulkChargePreviewMode: BulkChargePreviewMode =
    previewBulkChargeSegmentState.isError
      ? "fallback"
      : (resolvedBulkChargePreview?.studentCount ?? 0) > 0
        ? "live"
        : "empty";
  const bulkChargePreviewAlertMessage = previewBulkChargeSegmentState.isError
    ? "Live preview is unavailable; showing dashboard-based results. Final charged students may differ."
    : null;

  useEffect(() => {
    if (!isBulkChargeModalOpen) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      void previewBulkChargeSegment({
        grades: bulkChargeForm.grades,
        divisions: bulkChargeForm.divisions,
        gender: bulkChargeForm.gender,
        rateCategories: bulkChargeForm.rateCategories,
      });
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [
    bulkChargeForm.divisions,
    bulkChargeForm.gender,
    bulkChargeForm.grades,
    bulkChargeForm.rateCategories,
    isBulkChargeModalOpen,
    previewBulkChargeSegment,
  ]);

  useEffect(() => {
    if (
      !activeBulkChargeJob?.id ||
      activeBulkChargeJob.status !== "processing"
    ) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      void getBulkChargeJob({ bulkChargeJobId: activeBulkChargeJob.id })
        .unwrap()
        .then(async (job) => {
          const normalizedJob = {
            id: String(job.id ?? ""),
            status: String(job.status ?? ""),
            studentCount: Number(job.studentCount ?? 0),
            familyCount: Number(job.familyCount ?? 0),
            processedInvoiceCount: Number(job.processedInvoiceCount ?? 0),
          } satisfies BulkChargeJobSnapshot;
          setActiveBulkChargeJob(normalizedJob);
          if (normalizedJob.status === "complete") {
            window.clearInterval(interval);
            await dashboardRefetch();
            setActionFeedback(
              `Charged ${normalizedJob.studentCount} students across ${normalizedJob.familyCount} families.`,
            );
            setActionError(null);
            setIsBulkChargeModalOpen(false);
            setBulkChargeForm(buildBulkChargeDefaults());
          }
          if (normalizedJob.status === "failed") {
            window.clearInterval(interval);
            setActionError("Bulk charge processing failed.");
          }
        })
        .catch((error) => {
          setActionError(
            error instanceof Error
              ? error.message
              : "Unable to refresh bulk charge status.",
          );
        });
    }, 1500);

    return () => window.clearInterval(interval);
  }, [
    activeBulkChargeJob?.id,
    activeBulkChargeJob?.status,
    dashboardRefetch,
    getBulkChargeJob,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setShowExportMenu(false);
      }
      if (
        familyTableFiltersRef.current &&
        !familyTableFiltersRef.current.contains(event.target as Node)
      ) {
        setActiveFamilyTableFilterMenu(null);
      }
      if (
        familyActionMenuRef.current &&
        !familyActionMenuRef.current.contains(event.target as Node)
      ) {
        setActiveFamilyActionMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleAllRows = () => {
    if (selectedRows.size === displayedFamilies.length) {
      setSelectedRows(new Set());
      return;
    }
    setSelectedRows(
      new Set(displayedFamilies.map((family) => family.familyId)),
    );
  };

  const toggleRow = (familyId: string) => {
    setSelectedRows((current) => {
      const next = new Set(current);
      if (next.has(familyId)) {
        next.delete(familyId);
      } else {
        next.add(familyId);
      }
      return next;
    });
  };

  const runAction = async (actionId: string, action: () => Promise<string>) => {
    setBusyAction(actionId);
    setActionError(null);
    setActionFeedback(null);
    try {
      setActionFeedback(await action());
    } catch (actionFailure) {
      setActionError(
        actionFailure instanceof Error
          ? actionFailure.message
          : "Tuition action failed.",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const openDashboardAddChargeModal = (family: TuitionFamilySnapshot) => {
    setSelectedChargeFamily(family);
    setAddChargeForm(buildAddChargeDefaults());
    setIsAddChargeModalOpen(true);
    setActiveFamilyActionMenuId(null);
  };

  const openBulkChargeModal = () => {
    setBulkChargeForm(buildBulkChargeDefaults());
    setActiveBulkChargeJob(null);
    setIsBulkChargeModalOpen(true);
    setShowExportMenu(false);
  };

  const handleDashboardAddCharge = async () => {
    if (!selectedChargeFamily) {
      return;
    }

    try {
      await createAdHocCharge({
        tuitionAccountId: selectedChargeFamily.familyId,
        chargeName: addChargeForm.chargeName.trim(),
        amount: Number(addChargeForm.amount),
        perStudent: addChargeForm.perStudent,
        studentIds: addChargeForm.perStudent
          ? addChargeForm.studentIds
          : undefined,
        dueDate: addChargeForm.dueDate,
        billingCategory: addChargeForm.billingCategory,
        notes: addChargeForm.notes.trim() || undefined,
      }).unwrap();
      await dashboardRefetch();
      setActionFeedback(
        `Charge of ${formatCurrencyDollars(Number(addChargeForm.amount || 0))} added for ${selectedChargeFamily.familyName}.`,
      );
      setActionError(null);
      setIsAddChargeModalOpen(false);
      setSelectedChargeFamily(null);
      setAddChargeForm(buildAddChargeDefaults());
    } catch (error) {
      setAddChargeForm((current) => ({
        ...current,
        error:
          error && typeof error === "object" && "message" in error
            ? String(error.message)
            : "Unable to add this charge.",
      }));
    }
  };

  const handleBulkCharge = async () => {
    try {
      const job = await createBulkCharge({
        chargeName: bulkChargeForm.chargeName.trim(),
        amountPerStudent: Number(bulkChargeForm.amountPerStudent),
        dueDate: bulkChargeForm.dueDate,
        billingCategory: bulkChargeForm.billingCategory,
        notes: bulkChargeForm.notes.trim() || undefined,
        segmentFilters: {
          grades: bulkChargeForm.grades,
          divisions: bulkChargeForm.divisions,
          gender: bulkChargeForm.gender,
          rateCategories: bulkChargeForm.rateCategories,
        },
      }).unwrap();

      const normalizedJob = {
        id: String(job.id ?? ""),
        status: String(job.status ?? ""),
        studentCount: Number(
          job.studentCount ?? resolvedBulkChargePreview?.studentCount ?? 0,
        ),
        familyCount: Number(
          job.familyCount ?? resolvedBulkChargePreview?.familyCount ?? 0,
        ),
        processedInvoiceCount: Number(job.processedInvoiceCount ?? 0),
      } satisfies BulkChargeJobSnapshot;

      setActiveBulkChargeJob(normalizedJob);
      if (normalizedJob.status === "processing") {
        setActionFeedback(
          `Started bulk charge job for ${normalizedJob.studentCount} students.`,
        );
        setActionError(null);
        return;
      }

      await dashboardRefetch();
      setActionFeedback(
        `Charged ${normalizedJob.studentCount} students across ${normalizedJob.familyCount} families.`,
      );
      setActionError(null);
      setIsBulkChargeModalOpen(false);
      setBulkChargeForm(buildBulkChargeDefaults());
    } catch (error) {
      setBulkChargeForm((current) => ({
        ...current,
        error:
          error && typeof error === "object" && "message" in error
            ? String(error.message)
            : "Unable to run this bulk charge.",
      }));
    }
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div
      data-testid="school-tuition-source-faithful-root"
      data-route-family="internal-owned"
      data-route-ready="true"
      className="source-faithful-shell font-body"
      style={{ "--ystnj-source-faithful-max-width": "1440px" } as CSSProperties}
    >
      <TuitionToastStack toasts={toasts} onDismiss={dismissToast} />
      <div
        className="source-faithful-container"
        style={{ marginBlockStart: "0px" }}
      >
        <div className="group relative mb-2 w-full">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <SearchIcon className="h-5 w-5 text-text-tertiary transition-colors group-focus-within:text-primary" />
          </div>
          <input
            type="text"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search families, students, or type 'Status: Overdue'..."
            data-testid="school-tuition-global-search-input"
            className="block w-full rounded-[12px] border border-border bg-surface-light py-3.5 pl-11 pr-16 text-[15px] text-iron-grey shadow-sm transition-all placeholder:text-text-tertiary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
            <div className="hidden items-center gap-1 rounded-[6px] border border-border bg-muted px-2 py-1 font-mono text-[11px] font-medium text-text-secondary sm:flex">
              <CommandIcon className="h-3 w-3" />
              <span>K</span>
            </div>
          </div>
        </div>

        {snapshot ? (
          <>
            <div className="mb-6 grid min-w-0 grid-cols-1 items-stretch gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
              <DashboardSummaryPanel
                totalBilledCents={viewSummary.totalBilledCents}
                totalCollectedCents={viewSummary.totalCollectedCents}
                currentDueCents={currentDueCents}
                outstandingCents={viewSummary.overdueCents}
                collectionRate={viewSummary.collectionRate}
              />
              <CollectionTrendCard
                data={trendData}
                showCollectionTrend={showCollectionTrend}
                onToggle={() => setShowCollectionTrend((current) => !current)}
                emptyStateCopy={emptyDashboardCopy}
                hasCorrelatedStripeData={viewSummary.hasCorrelatedStripeData}
                academicYear={selectedAcademicYear}
                onAcademicYearChange={setSelectedAcademicYear}
                trendPreset={collectionTrendPreset}
                onTrendPresetChange={setCollectionTrendPreset}
                customStart={customRangeStart}
                customEnd={customRangeEnd}
                onCustomStartChange={setCustomRangeStart}
                onCustomEndChange={setCustomRangeEnd}
              />
            </div>

            {displayedFamilies.length > 0 ? (
              <>
                {selectedRows.size > 0 ? (
                  <div className="mb-4 flex flex-col justify-between gap-4 rounded-[8px] border border-border bg-surface-light p-3 shadow-sm sm:flex-row sm:items-center">
                    <div className="flex items-center gap-4 px-2">
                      <span className="text-sm font-semibold text-primary">
                        {selectedRows.size}{" "}
                        {selectedRows.size === 1 ? "family" : "families"}{" "}
                        selected
                      </span>
                      <div className="h-4 w-px bg-border" />
                      <button
                        type="button"
                        onClick={() => setSelectedRows(new Set())}
                        className="text-sm text-text-secondary transition-colors hover:text-iron-grey"
                      >
                        Clear selection
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-2 rounded-[6px] border border-border bg-surface-light px-3 py-1.5 text-[13px] font-medium text-iron-grey transition-colors hover:bg-muted">
                        <SendIcon className="h-4 w-4" />
                        Send reminder
                      </button>
                      <button className="flex items-center gap-2 rounded-[6px] border border-border bg-surface-light px-3 py-1.5 text-[13px] font-medium text-iron-grey transition-colors hover:bg-muted">
                        <DownloadIcon className="h-4 w-4" />
                        Export selected
                      </button>
                      <button className="flex items-center gap-2 rounded-[6px] border border-border bg-surface-light px-3 py-1.5 text-[13px] font-medium text-strawberry transition-colors hover:bg-error-background">
                        <ArchiveIcon className="h-4 w-4" />
                        Archive
                      </button>
                    </div>
                  </div>
                ) : null}

                <div
                  data-testid="school-tuition-family-accounts-table"
                  className="overflow-hidden rounded-[12px] border border-border bg-surface-light shadow-sm"
                >
                  <div
                    data-testid="school-tuition-controls"
                    className="border-b border-border p-4"
                  >
                    <div className="flex min-w-0 flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                      <div className="relative w-full min-w-0 flex-1 xl:max-w-sm">
                        <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
                        <input
                          type="text"
                          value={searchValue}
                          onChange={(event) =>
                            setSearchValue(event.target.value)
                          }
                          data-testid="school-tuition-table-search-input"
                          placeholder="Search families..."
                          className="w-full rounded-[6px] border border-border bg-background-light py-2 pl-9 pr-4 text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div className="flex min-w-0 flex-wrap items-center gap-2 xl:justify-end">
                        <div
                          ref={familyTableFiltersRef}
                          className="flex min-w-0 flex-wrap items-center gap-2"
                        >
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                setActiveFamilyTableFilterMenu((current) =>
                                  current === "status" ? null : "status",
                                )
                              }
                              className={`flex items-center gap-2 rounded-[6px] border px-3 py-2 text-[13px] font-medium transition-colors ${
                                selectedStatusFilters.size > 0
                                  ? "border-primary bg-primary/5 text-primary"
                                  : "border-border bg-surface-light text-iron-grey hover:bg-muted"
                              }`}
                            >
                              Status
                              <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
                            </button>
                            {activeFamilyTableFilterMenu === "status" ? (
                              <div className="absolute left-0 top-full z-20 mt-1 min-w-[180px] rounded-[8px] border border-border bg-surface-light py-1 shadow-lg">
                                {statusFilterOptions.map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => {
                                      setSelectedStatusFilters((current) => {
                                        const next = new Set(current);
                                        if (next.has(option)) {
                                          next.delete(option);
                                        } else {
                                          next.add(option);
                                        }
                                        return next;
                                      });
                                    }}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-[13px] text-iron-grey transition-colors hover:bg-muted"
                                  >
                                    <span>{option}</span>
                                    {selectedStatusFilters.has(option) ? (
                                      <CheckIcon className="h-4 w-4 text-primary" />
                                    ) : null}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                setActiveFamilyTableFilterMenu((current) =>
                                  current === "plan" ? null : "plan",
                                )
                              }
                              className={`flex items-center gap-2 rounded-[6px] border px-3 py-2 text-[13px] font-medium transition-colors ${
                                selectedPlanFilters.size > 0
                                  ? "border-primary bg-primary/5 text-primary"
                                  : "border-border bg-surface-light text-iron-grey hover:bg-muted"
                              }`}
                            >
                              Plan
                              <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
                            </button>
                            {activeFamilyTableFilterMenu === "plan" ? (
                              <div className="absolute left-0 top-full z-20 mt-1 min-w-[180px] rounded-[8px] border border-border bg-surface-light py-1 shadow-lg">
                                {planFilterOptions.map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => {
                                      setSelectedPlanFilters((current) => {
                                        const next = new Set(current);
                                        if (next.has(option)) {
                                          next.delete(option);
                                        } else {
                                          next.add(option);
                                        }
                                        return next;
                                      });
                                    }}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-[13px] text-iron-grey transition-colors hover:bg-muted"
                                  >
                                    <span>{option}</span>
                                    {selectedPlanFilters.has(option) ? (
                                      <CheckIcon className="h-4 w-4 text-primary" />
                                    ) : null}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                setActiveFamilyTableFilterMenu((current) =>
                                  current === "auto-pay" ? null : "auto-pay",
                                )
                              }
                              className={`flex items-center gap-2 rounded-[6px] border px-3 py-2 text-[13px] font-medium transition-colors ${
                                selectedAutoPayFilters.size > 0
                                  ? "border-primary bg-primary/5 text-primary"
                                  : "border-border bg-surface-light text-iron-grey hover:bg-muted"
                              }`}
                            >
                              Auto-pay
                              <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
                            </button>
                            {activeFamilyTableFilterMenu === "auto-pay" ? (
                              <div className="absolute left-0 top-full z-20 mt-1 min-w-[180px] rounded-[8px] border border-border bg-surface-light py-1 shadow-lg">
                                {autoPayFilterOptions.map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => {
                                      setSelectedAutoPayFilters((current) => {
                                        const next = new Set(current);
                                        if (next.has(option)) {
                                          next.delete(option);
                                        } else {
                                          next.add(option);
                                        }
                                        return next;
                                      });
                                    }}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-[13px] text-iron-grey transition-colors hover:bg-muted"
                                  >
                                    <span>{option}</span>
                                    {selectedAutoPayFilters.has(option) ? (
                                      <CheckIcon className="h-4 w-4 text-primary" />
                                    ) : null}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={openBulkChargeModal}
                          className="flex items-center gap-2 rounded-[6px] bg-[#2697a2] px-3 py-2 text-[13px] font-medium text-white transition-colors hover:bg-[#1d7e87]"
                        >
                          <DollarSignIcon className="h-4 w-4" />
                          Bulk charge
                        </button>
                        <div ref={exportMenuRef} className="relative">
                          <button
                            type="button"
                            onClick={() =>
                              setShowExportMenu((current) => !current)
                            }
                            className="flex items-center gap-2 rounded-[6px] border border-border bg-surface-light px-3 py-2 text-[13px] font-medium text-iron-grey transition-colors hover:bg-muted"
                          >
                            <DownloadIcon className="h-4 w-4 text-text-secondary" />
                            Export
                          </button>
                          {showExportMenu ? (
                            <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg">
                              <button
                                type="button"
                                onClick={() => {
                                  void runAction(
                                    "dashboard-export-csv",
                                    async () => {
                                      triggerDownload(
                                        new Blob(
                                          [
                                            buildDashboardExportCsv(
                                              displayedFamilies,
                                            ),
                                          ],
                                          { type: "text/csv;charset=utf-8" },
                                        ),
                                        "tuition-families.csv",
                                      );
                                      return "Tuition family CSV downloaded.";
                                    },
                                  );
                                  setShowExportMenu(false);
                                }}
                                className="block w-full px-4 py-2 text-left text-[13px] text-iron-grey transition-colors hover:bg-muted"
                              >
                                CSV
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  void runAction(
                                    "dashboard-export-pdf",
                                    async () => {
                                      await openTuitionFamiliesReportPdfInNewTab(
                                        {
                                          academicYear: selectedAcademicYear,
                                          generatedOnLabel,
                                          familyCount: displayedFamilies.length,
                                          summaryItems:
                                            dashboardExportSummaryItems,
                                          rows: dashboardExportPdfRows,
                                          fileName: "tuition-families.pdf",
                                        },
                                      );
                                      return "Tuition family PDF opened in a new tab.";
                                    },
                                  );
                                  setShowExportMenu(false);
                                }}
                                className="block w-full px-4 py-2 text-left text-[13px] text-iron-grey transition-colors hover:bg-muted"
                              >
                                PDF
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[980px] table-fixed border-collapse text-left">
                      <thead>
                        <tr className="border-b border-border bg-muted">
                          <th className="w-12 px-4 py-3">
                            <input
                              type="checkbox"
                              checked={
                                displayedFamilies.length > 0 &&
                                selectedRows.size === displayedFamilies.length
                              }
                              onChange={toggleAllRows}
                              className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                            />
                          </th>
                          <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                            Family name
                          </th>
                          <th className="w-14 px-2 py-3 text-center text-xs font-medium uppercase tracking-wider text-text-secondary">
                            <UsersRoundIcon className="mx-auto h-4 w-4" />
                          </th>
                          <th className="w-24 px-2 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                            Annual
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                            Paid
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                            Balance
                          </th>
                          <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                            Plan
                          </th>
                          <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                            Status
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-text-secondary">
                            Auto-pay
                          </th>
                          <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                            Due date
                          </th>
                          <th className="w-16 px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-text-secondary" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {displayedFamilies.map((family) => {
                          const dueIndicator = resolveDueDateIndicatorText(
                            family,
                            anchorIsoDate,
                          );
                          const tableStatusLabel =
                            resolveTableStatusLabel(family);
                          return (
                            <tr
                              key={family.familyId}
                              data-testid={`school-tuition-family-row-${family.familyId}`}
                              data-student-names={family.studentNames.join("|")}
                              className="group cursor-pointer transition-colors hover:bg-[#fbf9fa]"
                              onClick={() =>
                                navigate(`family/${family.familyId}`, {
                                  state: {
                                    familySnapshot: family,
                                  } satisfies SchoolTuitionFamilyNavigationState,
                                })
                              }
                            >
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedRows.has(family.familyId)}
                                  onChange={(event) => {
                                    event.stopPropagation();
                                    toggleRow(family.familyId);
                                  }}
                                  className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <div className="space-y-1.5">
                                  <button
                                    type="button"
                                    data-testid="school-tuition-family-select-button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      navigate(`family/${family.familyId}`, {
                                        state: {
                                          familySnapshot: family,
                                        } satisfies SchoolTuitionFamilyNavigationState,
                                      });
                                    }}
                                    className="font-body text-left text-sm font-semibold text-primary transition-colors hover:text-primary-hover"
                                  >
                                    {family.familyName}
                                  </button>
                                </div>
                              </td>
                              <td className="w-14 px-2 py-3 text-center font-body text-sm text-iron-grey">
                                <span className="border-b border-dashed border-text-tertiary">
                                  {family.studentCount}
                                </span>
                              </td>
                              <td className="w-24 truncate px-2 py-3 text-right font-body text-sm text-iron-grey whitespace-nowrap">
                                {formatCurrency(family.totalChargesCents)}
                              </td>
                              <td className="truncate px-4 py-3 text-right font-body text-sm text-iron-grey">
                                {formatCurrency(family.totalCreditsCents)}
                              </td>
                              <td className="truncate px-4 py-3 text-right font-body text-sm font-medium text-iron-grey">
                                {formatCurrency(family.netBalanceCents)}
                              </td>
                              <td className="truncate px-4 py-3 font-body text-sm text-text-secondary">
                                {resolveFamilyPlanLabel(family)}
                              </td>
                              <td
                                className="px-4 py-3"
                                data-testid="school-tuition-family-status"
                              >
                                <div className="flex items-center gap-2 font-body text-sm text-iron-grey">
                                  <span
                                    className="h-2 w-2 rounded-full"
                                    style={{
                                      backgroundColor:
                                        resolveStatusDotColor(family),
                                    }}
                                  />
                                  <span>{tableStatusLabel}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {family.autoPayEnrolled ? (
                                  <CheckCircleIcon className="mx-auto h-4 w-4 text-aquamarine" />
                                ) : (
                                  <span className="font-body text-sm text-text-tertiary">
                                    No
                                  </span>
                                )}
                              </td>
                              <td
                                data-testid="school-tuition-family-due-date"
                                className={`truncate px-4 py-3 font-body text-xs ${dueIndicator.className}`}
                              >
                                {dueIndicator.label}
                              </td>
                              <td className="relative px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setActiveFamilyActionMenuId((current) =>
                                      current === family.familyId
                                        ? null
                                        : family.familyId,
                                    );
                                  }}
                                  className={`mx-auto inline-flex items-center justify-center rounded-[6px] p-1.5 transition-colors hover:bg-border/50 hover:text-iron-grey ${
                                    activeFamilyActionMenuId === family.familyId
                                      ? "text-text-secondary opacity-100"
                                      : "pointer-events-none text-text-secondary opacity-0 group-hover:pointer-events-auto group-hover:opacity-100"
                                  }`}
                                  aria-label={`Actions for ${family.familyName}`}
                                >
                                  <MoreVerticalIcon className="h-4 w-4" />
                                </button>

                                {activeFamilyActionMenuId ===
                                family.familyId ? (
                                  <div
                                    ref={familyActionMenuRef}
                                    className="absolute right-4 top-11 z-20 w-56 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg"
                                  >
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setActiveFamilyActionMenuId(null);
                                        navigate(`family/${family.familyId}`, {
                                          state: {
                                            familySnapshot: family,
                                          } satisfies SchoolTuitionFamilyNavigationState,
                                        });
                                      }}
                                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                                    >
                                      <ArrowRightIcon className="h-4 w-4 text-text-secondary" />
                                      Open ledger
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openDashboardAddChargeModal(family);
                                      }}
                                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                                    >
                                      <DollarSignIcon className="h-4 w-4 text-text-secondary" />
                                      Add charge
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setActiveFamilyActionMenuId(null);
                                        navigate(`family/${family.familyId}`, {
                                          state: {
                                            familySnapshot: family,
                                            initialOverlay: "recurring-fees",
                                          } satisfies SchoolTuitionFamilyNavigationState,
                                        });
                                      }}
                                      className="flex w-full items-center gap-2 px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
                                    >
                                      <UsersRoundIcon className="h-4 w-4 text-text-secondary" />
                                      Manage recurring fees
                                    </button>
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div
                data-testid="school-tuition-family-accounts-empty"
                className="rounded-xl border border-dashed border-border px-6 py-6 text-sm text-text-secondary"
              >
                {emptyDashboardCopy ??
                  "No tuition family accounts are available for the active school dashboard yet."}
              </div>
            )}
          </>
        ) : null}

        <AddChargeModal
          isOpen={isAddChargeModalOpen}
          onClose={() => {
            setIsAddChargeModalOpen(false);
            setSelectedChargeFamily(null);
            setAddChargeForm(buildAddChargeDefaults());
          }}
          family={selectedChargeFamily}
          form={addChargeForm}
          onChange={(patch) =>
            setAddChargeForm((current) => ({ ...current, ...patch }))
          }
          onSubmit={() => {
            void handleDashboardAddCharge();
          }}
          busy={createAdHocChargeState.isLoading}
        />

        <BulkChargeModal
          isOpen={isBulkChargeModalOpen}
          onClose={() => {
            setIsBulkChargeModalOpen(false);
            setBulkChargeForm(buildBulkChargeDefaults());
            setActiveBulkChargeJob(null);
          }}
          form={bulkChargeForm}
          onChange={(patch) =>
            setBulkChargeForm((current) => ({ ...current, ...patch }))
          }
          onSubmit={() => {
            void handleBulkCharge();
          }}
          busy={createBulkChargeState.isLoading}
          preview={resolvedBulkChargePreview}
          previewLoading={isBulkChargePreviewLoading}
          previewMode={bulkChargePreviewMode}
          previewAlertMessage={bulkChargePreviewAlertMessage}
          previewErrorMessage={bulkChargePreviewErrorMessage}
          gradeOptions={gradeOptions}
          divisionOptions={divisionOptions}
          rateCategoryOptions={rateCategoryOptions}
          activeJob={activeBulkChargeJob}
        />
      </div>
    </div>
  );
}

export function SchoolTuitionOwnedRoutes({
  snapshotOverride = null,
  initialError = null,
  suppressSelectedFamilyDetails = false,
  initialFamilyLedgerOverlay = null,
}: SchoolTuitionOwnedPageProps = {}) {
  return (
    <Routes>
      <Route
        index
        element={
          <SchoolTuitionOwnedPage
            snapshotOverride={snapshotOverride}
            initialError={initialError}
            suppressSelectedFamilyDetails={suppressSelectedFamilyDetails}
          />
        }
      />
      <Route
        path="family/:familyId"
        element={
          <SchoolTuitionFamilyLedgerPage
            snapshotOverride={snapshotOverride}
            initialError={initialError}
            initialFamilyLedgerOverlay={initialFamilyLedgerOverlay}
          />
        }
      />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
